import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { api, dataUtils } from '../services/api';
import { FaRocket } from 'react-icons/fa';
import * as Sentry from "@sentry/react";
// Import components needed for deployment
import SEO from '../components/SEO';
import GPUSelection from '../components/vm/GPUSelection';
import LocationSelector from '../components/vm/LocationSelector';
import CloudInitModal from '../components/vm/CloudInitModal';
import GPUCountSelector from '../components/vm/GPUCountSelector';
import BootSourceSelector from '../components/vm/BootSourceSelector';
import AdvancedConfiguration from '../components/vm/AdvancedConfiguration';
import SSHKeySelector from '../components/vm/SSHKeySelector';
import SummaryCard from '../components/vm/SummaryCard';
import FirewallModal from '../components/Firewall/FirewallModal';
import SSHKeyModal from '../components/vm/SshKeyModal';
import ResponsiveWrapper from '../components/vm/ResponsiveWrapper';
import '../styling/custom.css';
import Sidebar from '../components/layout/Sidebar';
import '../styling/barrack.css'; 
// Import hooks
import { useWebSocket } from '../context/WebSocketContext';
// Import notification for deployment success only
import { useNotification } from '../Notifications';
const ADVANCED_TIER_ENABLED = true;

const ADVANCED_GPU_VALUES = ['H100-80GB', 'H100-SXM5-80GB', 'H200-141GB', 'B200-180GB', 'B300-262GB'];
// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'deploy-vm' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'deploy-vm' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'deploy-vm',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};
// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  // Authentication & Authorization
  'AUTH_ERROR': 'Please log in again to continue',
  'SESSION_EXPIRED': 'Your session has expired. Please log in again.',
  'UNAUTHORIZED_ACCESS': 'Access denied. Please check your permissions.',
  'USER_NOT_FOUND': 'User profile not found. Please contact support.',

  // Profile & Account
  'INCOMPLETE_PROFILE': 'Please complete your profile before deploying a VM.',
  'PROFILE_FETCH_FAILED': 'Unable to load profile settings',
  'RATE_LIMIT_EXCEEDED': 'Try again after few seconds',
  
  // VM Deployment
  'VM_DEPLOY_FAILED': 'Unable to deploy VM. Please try again.',
  'INVALID_VM_PARAMETERS': 'Invalid configuration. Please check your VM settings.',
  'CLOUD_INIT_VALIDATION_FAILED': 'Cloud-init script validation failed.',
  'VM_STATUS_FETCH_FAILED': 'Unable to check VM status. Please try again.',
  'NETWORK_ERROR': 'Unable to connect. Please check your internet connection and retry.',
  'VOLUME_ATTACHED': 'Boot volume is currently attached to another VM. Please detach it first or select a different volume.',

  // Credits & Billing
  'INSUFFICIENT_CREDITS': 'Insufficient credits to deploy this VM. Please add credits to continue.',

  // Resources & Data Loading
  'VOLUME_LIST_FAILED': 'Unable to load volumes. Please refresh the page.',
  'IMAGE_LIST_FAILED': 'Unable to load OS images',
  'FLAVORS_FETCH_FAILED': 'Unable to load GPU configurations',
  'STOCKS_FETCH_FAILED': 'Unable to load availability data',

  // SSH Keys
  'SSH_KEY_REQUIRED': 'SSH key is required for VM deployment',
  'SSH_KEY_LIMIT_EXCEEDED': 'SSH key limit reached. Please delete unused keys before creating new ones.',
  'SSH_KEY_NAME_EXISTS': 'An SSH key with this name already exists. Please choose a different name.',
  'INVALID_SSH_KEY_NAME': 'Invalid SSH key name. Please use only letters, numbers, hyphens, and underscores.',
  'INVALID_SSH_KEY_FORMAT': 'Invalid SSH key format. Please provide a valid public key.',
  'SSH_KEY_IMPORT_FAILED': 'Unable to import SSH key. Please try again.',
  'SSH_KEY_CREATE_FAILED': 'Unable to create SSH key. Please try again.',
  'SSH_KEY_LIST_FAILED': 'Unable to load SSH keys. Please refresh the page.',
  'SSH_KEY_FETCH_FAILED': 'Unable to retrieve SSH key details. Please try again.',

  // Scripts
  'SCRIPT_LIST_FAILED': 'Unable to load startup scripts',
  'SCRIPT_CREATE_FAILED': 'Unable to create startup script',
  'SCRIPT_UPDATE_FAILED': 'Unable to update startup script',
  'SCRIPT_DELETE_FAILED': 'Unable to delete startup script',
  'SCRIPT_REQUIRED': 'Startup script is required for deployment',
  'SCRIPT_NOT_FOUND': 'Script not found or you do not have permission to access it',
  'SCRIPT_NAME_EXISTS': 'A script with this name already exists. Please choose a different name.',
  'INVALID_SCRIPT_NAME': 'Script name can only contain letters, numbers, spaces, hyphens, underscores, and dots.',
  'INVALID_SCRIPT_CONTENT': 'Script content is invalid or exceeds the 16KB size limit.',
  'SCRIPT_VALIDATION_FAILED': 'Script validation failed. Please check the content format.',

  // Volume Creation Validation Errors
  'VOLUMES_INVALID_FORMAT': 'The volumes field must be an array of volume configurations',
  'VOLUME_NAME_MISSING': 'Each volume must have a name',
  'VOLUME_SIZE_MISSING': 'Each volume must have a size in GB',
  'VOLUME_TYPE_MISSING': 'Each volume must specify a type (HDD or NVMe)',
  'VOLUME_UNKNOWN_FIELD': 'Invalid volume field. Only name, size, and volume_type are allowed',
  'VOLUME_NAME_TOO_SHORT': 'Volume names must be at least 3 characters long',
  'VOLUME_NAME_TOO_LONG': 'Volume names cannot exceed 30 characters',
  'VOLUME_NAME_INVALID_CHARS': 'Volume names can only contain letters, numbers, hyphens, and underscores',
  'VOLUME_NAME_INVALID_FORMAT': 'Volume names cannot begin or end with hyphen or underscore',
  'DUPLICATE_VOLUME_NAME': 'Duplicate volume names are not allowed in the same request',
  'VOLUME_NAME_EXISTS': 'A volume with this name already exists in your account',
  'HDD_VOLUME_SIZE_EXCEEDED': 'HDD volumes cannot exceed 16,000GB',
  'NVME_VOLUME_SIZE_EXCEEDED': 'NVMe volumes cannot exceed 10,000GB',
  'INVALID_JSON': 'Invalid request format',
  'INVALID_USER_PROFILE': 'Invalid user profile format',
  'API_ERROR': 'Service temporarily unavailable',
  'SCRIPT_GET_FAILED': 'Unable to retrieve script. Please try again.',
  'SCRIPTS_DELETE_FAILED': 'Unable to delete scripts. Please try again.',
  
  // Existing Volume Attachment Errors
  'EXISTING_VOLUMES_INVALID_FORMAT': 'The existing_volumes field must be an array of volume IDs',
  'TOO_MANY_EXISTING_VOLUMES': 'Maximum 10 existing volumes allowed',
  'EXISTING_VOLUME_INVALID_FORMAT': 'Invalid volume ID format. Expected BRK-XXXXXXV',
  'EXISTING_VOLUME_LEGACY_REJECTED': 'Legacy tier volumes cannot be attached during deployment',
  'DUPLICATE_EXISTING_VOLUME': 'Duplicate volume IDs are not allowed',
  'EXISTING_VOLUME_NOT_FOUND': 'Volume not found or does not belong to your account',
  'BOOTABLE_VOLUME_NOT_ALLOWED': 'OS volumes cannot be attached as data volumes',
  'EXISTING_VOLUME_NOT_AVAILABLE': 'Volume is already attached to another VM',
  'EXISTING_VOLUME_WRONG_REGION': 'Volume not available in selected region',
  'VM_NAME_INVALID': 'Invalid VM name format',
  'VM_NAME_DUPLICATE': 'VM name already exists in this region',
  'VM_NAME_CHECK_FAILED': 'Unable to validate VM name',
  'VM_NAME_ALREADY_EXISTS': 'A VM with this name already exists in the selected region. Please choose a different name.',

  'VOLUME_ATTACHMENT_ERROR': 'Volume is already attached to another VM',
  'VOLUME_SIZE_INVALID': 'Volume size must be a number',
  'VOLUME_SIZE_TOO_SMALL': 'Volume size must be at least 100GB',

  // Volume ID Validation Errors
  'VOLUME_ID_INVALID_FORMAT': 'Invalid volume ID format. Expected BRK-XXXXXXV',
  'VOLUME_ID_LEGACY_REJECTED': 'Legacy tier volumes cannot be used for boot',
  'VOLUME_ID_NOT_BOOTABLE': 'Volume is not a bootable OS volume',
  'VOLUME_ID_NOT_AVAILABLE': 'Volume is already attached to another VM',
  'VOLUME_ID_WRONG_REGION': 'Volume not available in selected region',

  // Script & OS Volume Errors
  'SCRIPT_ID_INVALID_FORMAT': 'Invalid script ID format',
  'SCRIPT_ID_NOT_OWNED': 'Script not found or does not belong to your account',
  'OS_VOLUME_NOT_OWNED': 'OS volume not found or does not belong to your account',
  'OS_VOLUME_VALIDATION_FAILED': 'Unable to validate OS volume ownership',

  // Deployment Errors
  'CLOUD_INIT_INVALID': 'Cloud-init script validation failed',
  'INVALID_GPU_CONFIG': 'Invalid GPU configuration',
  'OUT_OF_STOCK': 'Selected GPU configuration is currently out of stock',

  // System
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
  'SECURITY_RULE_APPLICATION_FAILED': 'Security rules could not be applied',
  'STOCK_SERVICE_ERROR': 'GPU availability data temporarily unavailable. Please try again.',
  'STOCK_DATA_PROCESSING_ERROR': 'Unable to process availability data. Please refresh the page.',
  'STOCK_API_ERROR': 'Failed to retrieve GPU availability. Please try again.',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'UNEXPECTED_PAYLOAD': 'Invalid request format. Please try again.',
  'MISSING_FIELDS': 'Required fields are missing. Please check your input.',
  'INVALID_REQUEST': 'Invalid request format',
  'REQUEST_TOO_LARGE': 'Request size exceeds maximum allowed limit.',
  'IMAGE_NOT_FOUND': 'Selected OS image is not available.',
  'CONTACT_SUPPORT': 'Please contact support@barrack.ai for assistance.',
  'INVALID_REGION': 'Selected region is not available.',
  'COST_CALCULATION_FAILED': 'Unable to calculate deployment cost.',
  'FLAVOR_NOT_FOUND': 'No matching configuration found for selected GPU.',
  'SSH_KEY_NOT_FOUND': 'SSH key not found.',
  'SCRIPT_ID_NOT_FOUND': 'Startup script not found.',
  'SCRIPT_ID_VALIDATION_FAILED': 'Unable to validate script ID.',
  'VOLUME_NOT_FOUND': 'Volume not found.',
  'STOCK_DATA_UNAVAILABLE': 'Stock data temporarily unavailable.',
  'STOCK_CHECK_FAILED': 'Unable to verify GPU availability.',
  'INVALID_REGION_GPU_COMBINATION': 'Invalid GPU and region combination. Please select a compatible configuration.',
  'IMAGE_API_ERROR': 'Unable to load OS images. Please try again.',
  'API_CONNECTION_ERROR': 'Failed to connect to image service. Please try again later.',
  'SERVICE_ERROR': 'Service error occurred. Please try again.',
  'SCRIPT_UPDATE_CREATE_FAILED': 'Failed to create updated script. Please try again.',
  'DATABASE_RETRY_FAILED': 'Database operation failed after multiple retries. Please try again.',
  'SCRIPT_UPDATED_PARTIAL': 'Script updated but old version may still exist. Please refresh to sync.',
  'SERVER_ERROR': 'Service temporarily unavailable',
  'DATABASE_ERROR': 'Database error occurred. Please try again later.',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable',
  'METHOD_NOT_ALLOWED': 'Request method not allowed',

  // Network Errors (NEW)
  'OFFLINE': "You're offline. Check your connection.",
  'TIMEOUT': "Request timed out. Please try again.",
  'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
  'NO_RESPONSE': "No response from server. Please try again.",
  'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
  'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
};

const truncateText = (text, maxLength = 1000) => {
  if (!text || typeof text !== 'string') return '';
  return text.substring(0, maxLength);
};

const validateCloudInitScript = (script) => {
  if (script && typeof script === 'string' && script.length > 16384) {
    return { valid: false, error: 'Cloud-init script must be 16KB or less' };
  }
  return { valid: true };
};

const validateStartupScript = (script) => {
  if (!script || typeof script !== 'string') {
    return { valid: true };
  }
  
  const byteSize = new Blob([script]).size;
  if (byteSize > 16384) {
    return { valid: false, error: `Startup script must be 16KB or less (current: ${Math.round(byteSize / 1024)}KB)` };
  }
  return { valid: true };
};

const validateScriptName = (name, existingScripts = []) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Script name is required' };
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length === 0) {
    return { valid: false, error: 'Script name is required' };
  }
  
  if (trimmedName.length > 30) {
    return { valid: false, error: 'Script name must be 30 characters or less' };
  }
  
  if (!/^[a-zA-Z0-9_\-\s\.]+$/.test(trimmedName)) {
    return { valid: false, error: 'Script name can only contain letters, numbers, spaces, hyphens, underscores, and dots' };
  }
  
  const isDuplicate = existingScripts.some(
    script => script.name.toLowerCase() === trimmedName.toLowerCase()
  );
  
  if (isDuplicate) {
    return { valid: false, error: 'A script with this name already exists. Please choose a different name.' };
  }
  
  return { valid: true };
};
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Check error.error first (interceptor's user-friendly message)
  if (error.error && typeof error.error === 'string') {
    return error.error;
  }
  
  // Check direct error code 
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Handle HTTP status codes
  if (error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  if (error.status === 402) {
    return ERROR_MESSAGES['INSUFFICIENT_CREDITS'];
  }
  
  return 'Something went wrong. Please try again.';
};


const mapGpuTypeForAPI = (gpuTypeValue) => {
  if (!gpuTypeValue) return null;
  
  const mapping = {
    'H100-80G-PCIe': 'H100-PCIe-80GB',
    'H100-80G-PCIe-NVLink': 'H100-PCIe-NVLink-80GB',
    'H100-80GB': 'H100-SXM5-80GB',
    'A100-80G-PCIe': 'A100-PCIe-80GB',
    'A100-80G-PCIe-NVLink': 'A100-SXM4-80GB-NVLink',
    'RTX-A6000': 'RTX-A6000',
    'L40': 'L40',
    'A40': 'A40',
    'H100-SXM5-80GB': 'H100-SXM5-80GB',
    'B300-262GB': 'B300-262GB',
    'B200-180GB': 'B200-180GB',
    'H200-141GB': 'H200-141GB'
  };
  
  return mapping[gpuTypeValue] || gpuTypeValue;
};

const validateErrorObject = (error) => {
  if (!error || typeof error !== 'object') return false;
  return true;
};

// Responsive utilities
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getGridClasses = () => {
    if (isMobile) return 'grid grid-cols-1 gap-3';
    if (isTablet) return 'grid grid-cols-2 gap-4';
    return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
  };

  const getStatsCardClasses = () => {
    if (isMobile) return 'grid grid-cols-2 gap-2';
    if (isTablet) return 'grid grid-cols-4 gap-3';
    return 'grid grid-cols-1 md:grid-cols-4 gap-6';
  };

  return { isMobile, isTablet, getGridClasses, getStatsCardClasses };
};


// Reusable confirmation modal component
const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'Confirm', cancelText = 'Cancel', isDangerous = false }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#121212] rounded-lg w-full max-w-md border border-[#333333]">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-3">
            {title}
          </h3>
          <p className="text-gray-300 text-sm mb-6">
            {message}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white rounded transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 rounded font-medium transition-all ${
                isDangerous
                  ? 'bg-[rgba(238,85,85,0.05)] border border-[rgba(255,119,119,0.3)] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[rgba(255,85,85,0.5)] hover:text-[#ff5555] hover:shadow-[inset_0_0_20px_rgba(255,119,119,0.1)]'
                  : 'bg-[#e0eacc] hover:bg-[#c8d9a9] text-black'
              }`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Script Modal Component
const ScriptModal = React.memo(({ isOpen, type, onClose, onSave, editingScript, scripts }) => {
  const [scriptName, setScriptName] = useState('');
  const [scriptContent, setScriptContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [retryFetchScript, setRetryFetchScript] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialStateRef = useRef({ name: '', content: '' });
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (type === 'edit' && editingScript) {
        setScriptName(editingScript.name);
        const fetchFullScript = async () => {
          try {
            setIsLoading(true);
            const response = await api.getScript(editingScript.id);
            if (response && response.success && response.script) {
              setScriptContent(response.script.script || '');
            }
          } catch (error) {
            const errorMessage = getSafeErrorMessage(error);
            setModalError(errorMessage);
            
            if (!error.response || error.response.status >= 500) {
              setRetryFetchScript(() => fetchFullScript);
            }
          } finally {
            setIsLoading(false);
          }
        };
        fetchFullScript();
      } else {
        setScriptName('');
        setScriptContent('');
      }
      setModalError('');
    }
  }, [isOpen, type, editingScript]);

  useEffect(() => {
    if (isOpen) {
      initialStateRef.current = {
        name: type === 'edit' && editingScript ? editingScript.name : '',
        content: scriptContent
      };
      setHasUnsavedChanges(false);
    }
  }, [isOpen, type, editingScript]);

  useEffect(() => {
    if (isOpen) {
      const hasChanges = 
        scriptName !== initialStateRef.current.name ||
        scriptContent !== initialStateRef.current.content;
      setHasUnsavedChanges(hasChanges);
    }
  }, [scriptName, scriptContent, isOpen]);

  const handleSave = () => {
    const existingScripts = type === 'edit' && editingScript 
      ? scripts.filter(s => s.id !== editingScript.id)
      : scripts;
    
    const nameValidation = validateScriptName(scriptName, existingScripts);
    if (!nameValidation.valid) {
      setModalError(nameValidation.error);
      return;
    }

    if (!scriptContent.trim()) {
      setModalError('Script content is required');
      return;
    }

    const contentValidation = validateStartupScript(scriptContent);
    if (!contentValidation.valid) {
      setModalError(contentValidation.error);
      return;
    }

    onSave({
      name: scriptName.trim(),
      script: scriptContent.trim()
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#121212] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#333333]">
        <div className="p-4 border-b border-[#333333]">
          <h3 className="text-lg font-semibold text-[#e0eacc]">
            {type === 'create' ? 'Create New Script' : 'Edit Script'}
          </h3>
        </div>
        
        <div className="p-4">
          {modalError && (
            <div 
              className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-800 rounded-md"
              role="alert"
              aria-live="assertive"
              ref={(el) => el && el.focus()}
              tabIndex={-1}
            >
              <div className="flex items-center justify-between">
                <p className="text-red-400 text-sm">{modalError}</p>
                {retryFetchScript && (
                  <button
                    onClick={() => {
                      setModalError('');
                      retryFetchScript();
                    }}
                    className="ml-4 px-3 py-1 bg-red-700 hover:bg-red-600 text-white text-xs rounded transition-colors"
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
          )}     
          <div className="mb-4">
            <label className="block text-white text-sm font-medium mb-2">
              Script Name
            </label>
            <input
              type="text"
              value={scriptName}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[a-zA-Z0-9_\-\s\.]*$/.test(value)) {
                  setScriptName(value);
                }
              }}
              placeholder="Enter script name"
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#333333] rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#e0eacc] focus:border-transparent"
              maxLength={30}
              disabled={isLoading}
              aria-describedby="script-name-help"
            />
            <p id="script-name-help" className="mt-1 text-xs text-gray-400">
              {scriptName.length}/30 characters. Use only letters, numbers, spaces, hyphens, underscores, and dots.
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-white text-sm font-medium mb-2">
              Script Content
            </label>
            {isLoading ? (
              <div className="w-full h-64 bg-[#1a1a1a] border border-[#333333] rounded-md flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e0eacc]"></div>
              </div>
            ) : (
              <textarea
                value={scriptContent}
                onChange={(e) => setScriptContent(e.target.value)}
                placeholder="Enter your startup script content here..."
                className="w-full h-64 px-3 py-2 bg-[#1a1a1a] border border-[#333333] rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#e0eacc] focus:border-transparent font-mono text-sm"
                style={{ resize: 'vertical', minHeight: '200px' }}
              />
            )}
            <p className="mt-1 text-xs text-gray-400">
              {(() => {
                const byteSize = new Blob([scriptContent]).size;
                const sizeKB = (byteSize / 1024).toFixed(2);
                return `${sizeKB}/16 KB used. This script will run during VM initialization with root privileges.`;
              })()}
            </p>
          </div>
        </div>
        
        <div className="p-4 border-t border-[#333333] flex justify-end gap-2">
          <button
            onClick={() => {
              if (hasUnsavedChanges) {
                setShowUnsavedWarning(true);
              } else {
                onClose();
              }
            }}
            className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white rounded transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#e0eacc] hover:bg-[#c8d9a9] text-black rounded font-medium transition-colors"
            disabled={isLoading}
          >
            {type === 'create' ? 'Create Script' : 'Update Script'}
          </button>
        </div>
        
        <ConfirmationModal
          isOpen={showUnsavedWarning}
          title="Unsaved Changes"
          message="You have unsaved changes to this script. Are you sure you want to discard them?"
          confirmText="Discard Changes"
          cancelText="Keep Editing"
          isDangerous={true}
          onConfirm={() => {
            setShowUnsavedWarning(false);
            setHasUnsavedChanges(false);
            onClose();
          }}
          onCancel={() => {
            setShowUnsavedWarning(false);
          }}
        />
      </div>
    </div>
  );
});

ScriptModal.displayName = 'ScriptModal';


const DeployVM = () => {
  const { isMobile, isTablet, getGridClasses, getStatsCardClasses } = useResponsive();
  
  const notification = useNotification();
  const navigate = useNavigate();

  const { isAuthenticated, handleAuthError } = useAuth();

  const { 
    stocks, 
    flavors, 
    error: wsError 
  } = useWebSocket();
  
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [userHasSelectedCount, setUserHasSelectedCount] = useState(false);

  const [loadingStates, setLoadingStates] = useState({
    main: true,
    gpu: false,
    location: false,
    gpuCount: false,
    summary: false,
    deploying: false,
    scripts: false
  });

  const [securityRules, setSecurityRules] = useState([]);
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState(''); 
  
  const [showRetryProfile, setShowRetryProfile] = useState(false);
  const [showRetryDeploy, setShowRetryDeploy] = useState(false);
  const [showRetrySSHKeys, setShowRetrySSHKeys] = useState(false);
  const [showRetryScripts, setShowRetryScripts] = useState(false);
  const [showRetryVolumes, setShowRetryVolumes] = useState(false);
  const [showRetryImages, setShowRetryImages] = useState(false);

  const [gpuTypes, setGpuTypes] = useState([]);

  const [selectedGPU, setSelectedGPU] = useState('');
  
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  
  const [gpuCounts, setGpuCounts] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [sshKeysLoaded, setSSHKeysLoaded] = useState(false);

  const [selectedBootSource, setSelectedBootSource] = useState('os_images');
  const [bootSources, setBootSources] = useState([]);
  const [osVolumeSize, setOsVolumeSize] = useState(100);
  const [newVolumes, setNewVolumes] = useState([]);
  const [selectedAttachableVolumes, setSelectedAttachableVolumes] = useState([]);

  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState('');
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [scriptModalOpen, setScriptModalOpen] = useState(false);
  const [scriptModalType, setScriptModalType] = useState('create');
  const [editingScript, setEditingScript] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState(null);

  useEffect(() => {
    logInfo('DeployVM component mounted', {
      isAuthenticated,
      isMobile,
      isTablet,
      hasWebSocketData: !!(stocks && flavors)
    });
  }, []);

  useEffect(() => {
    const sources = isAuthenticated 
      ? [
          { id: 'os_images', name: 'OS Images' },
          { id: 'volumes', name: 'Volumes' },
          { id: 'custom_images', name: 'Custom Images' }
        ]
      : [
          { id: 'os_images', name: 'OS Images' }
        ];
    
    setBootSources(sources);
    
    if (!isAuthenticated && (selectedBootSource === 'volumes' || selectedBootSource === 'custom_images')) {
      logUserAction('boot_source_reset_unauthenticated', {
        previousSource: selectedBootSource,
        newSource: 'os_images'
      });
      setSelectedBootSource('os_images');
    }
  }, [isAuthenticated, selectedBootSource]);

  const [osVersions, setOsVersions] = useState([]);
  const [selectedOS, setSelectedOS] = useState('');
  const [priceCurrency, setPriceCurrency] = useState('USD');

  const [volumes, setVolumes] = useState([]);
  const [selectedVolume, setSelectedVolume] = useState('');
  const [bootableVolumes, setBootableVolumes] = useState([]);
  const [nonBootableVolumes, setNonBootableVolumes] = useState([]);

  const [customImages, setCustomImages] = useState([]);
  const [selectedCustomImage, setSelectedCustomImage] = useState('');

  const [publicIP, setPublicIP] = useState(true);

  const [sshKeys, setSSHKeys] = useState([]);
  const [selectedSSHKey, setSelectedSSHKey] = useState('');
  
  const [cloudInitScript, setCloudInitScript] = useState('');
  
  const [flavorsData, setFlavorsData] = useState([]);
  const [stocksData, setStocksData] = useState([]);
  const [configuration, setConfiguration] = useState({});
  const [price, setPrice] = useState('€0.00/minute');
  const [deployEnabled, setDeployEnabled] = useState(false);
  const [userCurrency, setUserCurrency] = useState('USD');
const [isProfileComplete, setIsProfileComplete] = useState(true);
const [creditBalance, setCreditBalance] = useState(null);
const [balanceLoaded, setBalanceLoaded] = useState(false);  
  const [confirmDeploy, setConfirmDeploy] = useState(false);
  
  const [firewallModalOpen, setFirewallModalOpen] = useState(false);
  const [sshKeyModalOpen, setSSHKeyModalOpen] = useState(false);
  const [sshKeyModalType, setSSHKeyModalType] = useState('create');
  const [cloudInitModalOpen, setCloudInitModalOpen] = useState(false);

  const isAdvancedRegion = useMemo(() => {
    return selectedRegion?.includes('finland');
  }, [selectedRegion]);

  const deploymentTimeoutRef = useRef(null);
  const lastDeployAttemptRef = useRef(0);
  const DEPLOY_COOLDOWN = 2000;

  useEffect(() => {
    Sentry.setContext("deploy-vm", {
      isAuthenticated,
      selectedGPU,
      selectedRegion,
      selectedCount,
      selectedBootSource,
      publicIP,
      isMobile,
      isTablet,
      initialDataLoaded,
      isAdvancedRegion,
      selectedScript
    });
  }, [isAuthenticated, selectedGPU, selectedRegion, selectedCount, selectedBootSource, 
      publicIP, isMobile, isTablet, initialDataLoaded, isAdvancedRegion, selectedScript]);

const visibleGpuTypes = useMemo(() => {
    // Step 1: Filter out advanced tier GPUs if disabled
    let filtered = ADVANCED_TIER_ENABLED
      ? gpuTypes
      : gpuTypes.filter(gpu => !ADVANCED_GPU_VALUES.includes(gpu.value));

    // Step 2: Remove GPUs that have zero available locations in stock data
    if (stocksData && Array.isArray(stocksData) && stocksData.length > 0) {
      filtered = filtered.filter(gpu => {
        const gpuTypeValue = gpu.value;
        if (!gpuTypeValue) return false;

        const ADVANCED_GPUS = ['H100-80GB', 'H100-SXM5-80GB', 'H200-141GB', 'B200-180GB', 'B300-262GB'];
        const isAdvancedGpu = ADVANCED_GPUS.includes(gpuTypeValue) ||
          gpuTypeValue.includes('SXM') ||
          gpuTypeValue.includes('H200') ||
          gpuTypeValue.includes('B200') ||
          gpuTypeValue.includes('B300');

        const selectedBase = gpuTypeValue.split('-')[0].toUpperCase();

        // Check if any region has this GPU available
        return stocksData.some(regionStock => {
          if (!regionStock || !regionStock.region || !regionStock.models || !Array.isArray(regionStock.models)) return false;

          const isAdvancedRegion = regionStock.region.toLowerCase().includes('finland');
          if (isAdvancedGpu && !isAdvancedRegion) return false;
          if (!isAdvancedGpu && isAdvancedRegion) return false;

          return regionStock.models.some(model => {
            if (!model || !model.model) return false;

            const modelName = model.model;
            const isAdvancedFormat = /-(1|2|4|8)x$/.test(modelName);

            let isMatch = false;
            if (isAdvancedFormat) {
              const stockBase = modelName.split('-')[0].toUpperCase();
              isMatch = isAdvancedGpu && stockBase === selectedBase;
            } else {
              isMatch = !isAdvancedGpu && modelName === gpuTypeValue;
            }

            if (!isMatch) return false;

            // Check if at least one configuration is available
            if (model.configurations) {
              if (!Array.isArray(model.configurations)) {
                return Object.values(model.configurations).some(status =>
                  status === 'available' || status === 'in_stock' || (typeof status === 'number' && status > 0)
                );
              } else {
                return model.configurations.some(config =>
                  config.status === 'available' || config.status === 'in_stock'
                );
              }
            }

            return model.available === 'available' || model.available === 'in_stock';
          });
        });
      });
    }

    return filtered;
  }, [gpuTypes, stocksData]);

  // Ensure selectedGPU is always within the visible list
useEffect(() => {
  if (visibleGpuTypes.length === 0) return;
  
  const currentIsVisible = visibleGpuTypes.some(gpu => gpu.id === selectedGPU);
  if (!currentIsVisible) {
    setSelectedGPU(visibleGpuTypes[0].id);
  }
}, [visibleGpuTypes, selectedGPU]);

  const checkDeployRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastDeploy = now - lastDeployAttemptRef.current;
    
    if (timeSinceLastDeploy < DEPLOY_COOLDOWN) {
      logError('Deploy attempt blocked - rate limit exceeded', {
        timeSinceLastDeploy,
        cooldownPeriod: DEPLOY_COOLDOWN
      });
      return false;
    }
    
    lastDeployAttemptRef.current = now;
    return true;
  }, []);

const fetchSpecsAndStocks = useCallback(async () => {
  setLoadingStates(prev => ({ 
    ...prev, 
    gpu: true, 
    location: true, 
    gpuCount: true 
  }));
  
  try {
    const [specsResponse, stocksResponse] = await Promise.all([
      api.getBarrackSpecs(),
      api.getBarrackStocks()
    ]);
    
    let transformedFlavors = [];
    if (specsResponse?.success && Array.isArray(specsResponse.data)) {
      transformedFlavors = specsResponse.data.map(spec => ({
        name: spec.tier === 'legacy' ? `n3-${spec.gpu_type}x${spec.gpu_count}` : `${spec.gpu_count}${spec.gpu_type}`,
        region: spec.region,
        region_name: spec.region,
        cpu: spec.cpu,
        ram: spec.ram,
        disk: typeof spec.disk === 'object' ? spec.disk.min : spec.disk,
        ephemeral: spec.ephemeral,
        gpu: spec.gpu_type,
        gpu_count: spec.gpu_count,
        available: true,
        pricing: spec.price
      }));
    }
    
    let transformedStocks = [];
    if (stocksResponse?.status && Array.isArray(stocksResponse.data)) {
      const regionMap = new Map();
      
      stocksResponse.data.forEach(stock => {
        const normalizedRegion = stock.region.replace(/\s+/g, '-');
        
        if (!regionMap.has(normalizedRegion)) {
          regionMap.set(normalizedRegion, { region: normalizedRegion, models: [] });
        }
        
        const regionData = regionMap.get(normalizedRegion);
        
        if (stock.tier === 'advanced') {
          stock.configurations.forEach(config => {
            const baseModel = stock.model.split('-')[0];
            const modelName = `${baseModel}-${config.gpu_count}x`;
            
            regionData.models.push({
              model: modelName,
              available: config.status === 'available' ? 'available' : 'out_of_stock',
              configurations: {
                [`${config.gpu_count}x`]: config.status === 'available' ? 'available' : 'out_of_stock'
              }
            });
          });
        } else {
          regionData.models.push({
            model: stock.model,
            available: stock.configurations.some(c => c.status === 'available') ? 'available' : 'out_of_stock',
            configurations: stock.configurations.reduce((acc, config) => {
              acc[`${config.gpu_count}x`] = config.status === 'available' ? 'available' : 'out_of_stock';
              return acc;
            }, {})
          });
        }
      });
      
      transformedStocks = Array.from(regionMap.values()).map(region => ({
        ...region,
        models: region.models.map(model => ({
          ...model,
          model: model.model
            .replace('A100-PCIe-80GB', 'A100-80G-PCIe')
            .replace('H100-PCIe-80GB', 'H100-80G-PCIe')
            .replace('H100-PCIe-NVLink-80GB', 'H100-80G-PCIe-NVLink')
        }))
      }));
    }
    
    if (transformedFlavors.length > 0) {
      setFlavorsData(transformedFlavors);
      const [processedData, processedRegions] = await Promise.all([
        dataUtils.processFlavorData(transformedFlavors),
        dataUtils.getRegionsFromFlavors(transformedFlavors)
      ]);
      
      if (processedData?.gpuTypes) {
        setGpuTypes(processedData.gpuTypes);
        if (processedData.gpuTypes.length > 0 && !selectedGPU) {
          setSelectedGPU(processedData.gpuTypes[0].id);
        }
      }
      
      if (Array.isArray(processedRegions)) {
        setRegions(processedRegions);
        if (processedRegions.length > 0 && !selectedRegion) {
          setSelectedRegion(processedRegions[0].id);
        }
      }
    }
    
    if (transformedStocks.length > 0) {
      setStocksData(transformedStocks);
    }
    
    setLoadingStates(prev => ({ ...prev, gpu: false, location: false, gpuCount: false }));
} catch (error) {
    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      setLoadingStates(prev => ({ ...prev, gpu: false, location: false, gpuCount: false }));
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || 'Something went wrong. Please try again.';
      notification.error(errorMessage);
      logError('Error in fetchSpecsAndStocks', { 
        code: error.code,
        status: error.status
      }, error);
      setLoadingStates(prev => ({ ...prev, gpu: false, location: false, gpuCount: false }));
      return;
    }
    
    // Position 3: Fallback
    notification.error(getSafeErrorMessage(error));
    setLoadingStates(prev => ({ ...prev, gpu: false, location: false, gpuCount: false }));
  
  }
}, [selectedGPU, selectedRegion, handleAuthError]);

const fetchUserCurrency = useCallback(async () => {
  if (!isAuthenticated) return;
  
  const startTime = performance.now();
  
  try {
    logInfo('Fetching user currency preference', { isAuthenticated });
    
    const response = await api.getUserProfile();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success && response.profile) {
      if (response.profile.preferred_currency) {
        setUserCurrency(response.profile.preferred_currency);
      }
      
      setIsProfileComplete(response.profile.is_profile_complete || false);
      
      logPerformance('fetch_user_currency', duration, {
        success: true,
        currency: response.profile.preferred_currency,
        profileComplete: response.profile.is_profile_complete
      });
      
      logInfo('User currency preference loaded successfully', {
        currency: response.profile.preferred_currency,
        profileComplete: response.profile.is_profile_complete,
        duration: `${duration.toFixed(2)}ms`
      });
    } else {
      logPerformance('fetch_user_currency', duration, {
        success: false,
        fallbackCurrency: 'USD'
      });
    }
 } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['PROFILE_FETCH_FAILED'];
      setError(errorMessage);
      setShowRetryProfile(true);
      logError('Error in fetchUserCurrency', { 
        code: error.code,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      return;
    }
    
    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['PROFILE_FETCH_FAILED'];
    setError(errorMessage);
    setShowRetryProfile(true);
    logError('Failed to fetch user currency', {
      duration: `${duration.toFixed(2)}ms`,
      fallbackCurrency: 'USD'
    }, error);
  
  }
}, [isAuthenticated, handleAuthError]);


const fetchCreditBalance = useCallback(async () => {
  if (!isAuthenticated) {
    setCreditBalance(null);
    setBalanceLoaded(false);
    return;
  }

  try {
    const response = await api.getCreditBalance();

    if (response && response.success && response.balance) {
      setCreditBalance(response.balance);
      setBalanceLoaded(true);

      logInfo('Credit balance loaded', {
        currency: response.account_currency
      });
    }
  } catch (error) {
    if (error.status === 401) {
      handleAuthError();
      return;
    }

    if (error.code) {
      logError('Credit balance fetch error', { code: error.code }, error);
      return;
    }

    logError('Credit balance fetch error', {}, error);
  }
}, [isAuthenticated, handleAuthError]);
  const retryFetchProfile = useCallback(() => {
    setError(null);
    setLastShownError('');
    setShowRetryProfile(false);
    fetchUserCurrency();
  }, [fetchUserCurrency]);

useEffect(() => {
    fetchUserCurrency();
  }, [fetchUserCurrency]);

  useEffect(() => {
    fetchCreditBalance();
  }, [fetchCreditBalance]);

  useEffect(() => {
    window.debugDeployButton = () => {
      const selectedGpuObj = gpuTypes.find(gpu => gpu.id === selectedGPU);
      const gpuTypeValue = selectedGpuObj ? selectedGpuObj.value : '';
      const selectedRegionObj = regions.find(region => region.id === selectedRegion);
      const regionValue = selectedRegionObj ? selectedRegionObj.value : '';
          
      const isAvailable = stocksData && Array.isArray(stocksData) ? 
        dataUtils.checkAvailability(stocksData, gpuTypeValue, regionValue, selectedCount) : false;
      
      const flavorDetails = dataUtils.getFlavorDetails(flavorsData, gpuTypeValue, regionValue, selectedCount);
      
      let sshKeyValid = true;
      if (isAuthenticated) {
        if (isAdvancedRegion) {
          const isVolumeBoot = (selectedBootSource === 'volumes' && selectedVolume) || 
                               (osVersions.find(os => os.id === selectedOS)?.is_user_volume);
          
          if (isVolumeBoot) {
            sshKeyValid = !selectedSSHKey;
          } else {
            sshKeyValid = !!selectedSSHKey;
          }
        } else {
          sshKeyValid = !!selectedSSHKey;
        }
      }    
      
      let bootSourceValid = false;
      if (selectedBootSource === 'os_images' && selectedOS) {
        bootSourceValid = true;
      } else if (selectedBootSource === 'volumes' && selectedVolume) {
        bootSourceValid = bootableVolumes.some(vol => vol.id === selectedVolume);
      }
      
      const calculatedDeployEnabled = isAvailable && flavorDetails && sshKeyValid && bootSourceValid;
      
      return {
        isAuthenticated,
        isProfileComplete,
        selectedGPU,
        gpuTypeValue,
        selectedRegion,
        regionValue,
        selectedCount,
        isAvailable,
        flavorDetails: !!flavorDetails,
        sshKeyValid,
        selectedSSHKey,
        bootSourceValid,
        selectedBootSource,
        selectedOS,
        selectedVolume,
        selectedScript,
        isAdvancedRegion,
        calculatedDeployEnabled,
        deployEnabled,
        stocksData: stocksData?.length || 0,
        flavorsData: flavorsData?.length || 0
      };
    };
    
    window.deployVMState = {
      isAuthenticated,
      isProfileComplete,
      selectedGPU,
      selectedRegion,
      selectedCount,
      selectedBootSource,
      selectedOS,
      selectedVolume,
      selectedSSHKey,
      selectedScript,
      deployEnabled,
      loadingStates,
      initialDataLoaded,
      gpuTypes,
      regions,
      osVersions,
      bootableVolumes,
      sshKeys,
      scripts,
      stocksData,
      flavorsData,
      isAdvancedRegion
    };
  }, [isAuthenticated, isProfileComplete, selectedGPU, selectedRegion, selectedCount, 
      selectedBootSource, selectedOS, selectedVolume, selectedSSHKey, selectedScript, deployEnabled, 
      loadingStates, initialDataLoaded, gpuTypes, regions, osVersions, bootableVolumes, 
      sshKeys, scripts, stocksData, flavorsData, isAdvancedRegion]);

  useEffect(() => {
    if (error && error !== lastShownError) {
      setLastShownError(error);
    }
  }, [error, lastShownError]);
useEffect(() => {
  const fetchData = async () => {
    const startTime = performance.now();
    
    if (!initialDataLoaded) {
      setLoadingStates(prev => ({ ...prev, main: true }));
      logInfo('Initial data load started');
    } else {
      setLoadingStates(prev => ({ 
        ...prev, 
        gpu: true, 
        location: true, 
        gpuCount: true, 
        summary: true 
      }));
      logInfo('Data refresh started');
    }
    
    try {
      if (flavors && Array.isArray(flavors) && flavors.length > 0 && stocks && Array.isArray(stocks) && stocks.length > 0) {
        logInfo('Using WebSocket data for initialization', {
          flavorsCount: flavors.length,
          stocksCount: stocks.length
        });
        
        setFlavorsData(flavors);
        setStocksData(stocks);
        
        try {
          const processingStartTime = performance.now();
          const [processedData, processedRegions] = await Promise.all([
            dataUtils.processFlavorData(flavors),
            dataUtils.getRegionsFromFlavors(flavors)
          ]);
          const processingEndTime = performance.now();
          const processingDuration = processingEndTime - processingStartTime;

          if (processedData && processedData.gpuTypes) {
            setGpuTypes(processedData.gpuTypes);
           if (processedData.gpuTypes.length > 0 && !selectedGPU) {
  const eligibleGpus = ADVANCED_TIER_ENABLED 
    ? processedData.gpuTypes 
    : processedData.gpuTypes.filter(gpu => !ADVANCED_GPU_VALUES.includes(gpu.value));
  const defaultGPU = eligibleGpus.length > 0 ? eligibleGpus[0].id : processedData.gpuTypes[0].id;
              setSelectedGPU(defaultGPU);
              
              logUserAction('gpu_auto_selected', {
                gpuId: defaultGPU,
                gpuType: processedData.gpuTypes[0].label
              });
            }
            setLoadingStates(prev => ({ ...prev, gpu: false }));
          }

          if (Array.isArray(processedRegions)) {
            setRegions(processedRegions);
            if (processedRegions.length > 0 && !selectedRegion) {
              const defaultRegion = processedRegions[0].id;
              setSelectedRegion(defaultRegion);
              
              logUserAction('region_auto_selected', {
                regionId: defaultRegion,
                regionName: processedRegions[0].name
              });
            }
            setLoadingStates(prev => ({ ...prev, location: false }));
          }

          logPerformance('process_flavor_data', processingDuration, {
            success: true,
            gpuTypesCount: processedData?.gpuTypes?.length || 0,
            regionsCount: processedRegions?.length || 0
          });
          
        } catch (processingError) {
          notification.error(getSafeErrorMessage(processingError));
          
          logError('Data processing failed', {
            flavorsCount: flavors.length,
            operation: 'processFlavorData'
          }, processingError);
          
          setLoadingStates(prev => ({ ...prev, gpu: false, location: false }));
        }
        
        setLoadingStates(prev => ({ ...prev, gpuCount: false }));
        
        if (!initialDataLoaded) {
          setLoadingStates(prev => ({ ...prev, main: false }));
        }
        setInitialDataLoaded(true);
      } else {
        logInfo('WebSocket data not available, fetching from API');
        
        try {
          setLoadingStates(prev => ({ 
            ...prev, 
            gpu: true, 
            location: true, 
            gpuCount: true, 
            summary: true 
          }));
          
          const apiStartTime = performance.now();
          const [specsResponse, stocksResponse] = await Promise.all([
            api.getBarrackSpecs(),
            api.getBarrackStocks()
          ]);
          const apiEndTime = performance.now();
          const apiDuration = apiEndTime - apiStartTime;
          
          let transformedFlavors = [];
          if (specsResponse?.success && Array.isArray(specsResponse.data)) {
            transformedFlavors = specsResponse.data.map(spec => ({
              name: spec.tier === 'legacy' ? `n3-${spec.gpu_type}x${spec.gpu_count}` : `${spec.gpu_count}${spec.gpu_type}`,
              region: spec.region,
              region_name: spec.region,
              cpu: spec.cpu,
              ram: spec.ram,
              disk: typeof spec.disk === 'object' ? spec.disk.min : spec.disk,
              ephemeral: spec.ephemeral,
              gpu: spec.gpu_type,
              gpu_count: spec.gpu_count,
              available: true,
              pricing: spec.price
            }));
          }
          
          let transformedStocks = [];
          if (stocksResponse?.status && Array.isArray(stocksResponse.data)) {
            const regionMap = new Map();
            
            stocksResponse.data.forEach(stock => {
              const normalizedRegion = stock.region.replace(/\s+/g, '-');
              
              if (!regionMap.has(normalizedRegion)) {
                regionMap.set(normalizedRegion, { region: normalizedRegion, models: [] });
              }
              
              const regionData = regionMap.get(normalizedRegion);
              if (stock.tier === 'advanced') {
                stock.configurations.forEach(config => {
                  const baseModel = stock.model.split('-')[0];
                  const modelName = `${baseModel}-${config.gpu_count}x`;
                  
                  regionData.models.push({
                    model: modelName,
                    available: config.status === 'available' ? 'available' : 'out_of_stock',
                    configurations: {
                      [`${config.gpu_count}x`]: config.status === 'available' ? 'available' : 'out_of_stock'
                    }
                  });
                });
              } else {
                regionData.models.push({
                  model: stock.model,
                  available: stock.configurations.some(c => c.status === 'available') ? 'available' : 'out_of_stock',
                  configurations: stock.configurations.reduce((acc, config) => {
                    acc[`${config.gpu_count}x`] = config.status === 'available' ? 'available' : 'out_of_stock';
                    return acc;
                  }, {})
                });
              }
            });
            
            transformedStocks = Array.from(regionMap.values()).map(region => ({
              ...region,
              models: region.models.map(model => ({
                ...model,
                model: model.model
                  .replace('A100-PCIe-80GB', 'A100-80G-PCIe')
                  .replace('H100-PCIe-80GB', 'H100-80G-PCIe')
                  .replace('H100-PCIe-NVLink-80GB', 'H100-80G-PCIe-NVLink')
              }))
            }));
          }
          
          if (transformedFlavors.length > 0) {
            setFlavorsData(transformedFlavors);
          }
          if (transformedStocks.length > 0) {
            setStocksData(transformedStocks);
          }

          logPerformance('fetch_specs_stocks', apiDuration, {
            success: true,
            specsCount: transformedFlavors.length,
            stocksCount: transformedStocks.length
          });
          
          if (transformedFlavors.length > 0) {
            try {
              const processingStartTime = performance.now();
              const [processedData, processedRegions] = await Promise.all([
                dataUtils.processFlavorData(transformedFlavors),
                dataUtils.getRegionsFromFlavors(transformedFlavors)
              ]);
              const processingEndTime = performance.now();
              const processingDuration = processingEndTime - processingStartTime;

              if (processedData && processedData.gpuTypes) {
                setGpuTypes(processedData.gpuTypes);
        if (processedData.gpuTypes.length > 0 && !selectedGPU) {
  const eligibleGpus = ADVANCED_TIER_ENABLED 
    ? processedData.gpuTypes 
    : processedData.gpuTypes.filter(gpu => !ADVANCED_GPU_VALUES.includes(gpu.value));
  const defaultGPU = eligibleGpus.length > 0 ? eligibleGpus[0].id : processedData.gpuTypes[0].id;
                  setSelectedGPU(defaultGPU);
                  
                  logUserAction('gpu_auto_selected_api', {
                    gpuId: defaultGPU,
                    gpuType: processedData.gpuTypes[0].label
                  });
                }
              }

              if (Array.isArray(processedRegions)) {
                setRegions(processedRegions);
                if (processedRegions.length > 0 && !selectedRegion) {
                  const defaultRegion = processedRegions[0].id;
                  setSelectedRegion(defaultRegion);
                  
                  logUserAction('region_auto_selected_api', {
                    regionId: defaultRegion,
                    regionName: processedRegions[0].name
                  });
                }
              }

              setLoadingStates(prev => ({ ...prev, gpu: false, location: false }));

              logPerformance('process_flavor_data_api', processingDuration, {
                success: true,
                gpuTypesCount: processedData?.gpuTypes?.length || 0,
                regionsCount: processedRegions?.length || 0
              });
              
            } catch (processingError) {
              notification.error(getSafeErrorMessage(processingError));
              
              logError('API data processing failed', {
                flavorsCount: transformedFlavors.length,
                operation: 'processFlavorData'
              }, processingError);
              
              setLoadingStates(prev => ({ ...prev, gpu: false, location: false }));
            }
          }
          
          setLoadingStates(prev => ({ 
            ...prev, 
            gpuCount: false, 
            main: false 
          }));
          setInitialDataLoaded(true);
} catch (apiError) {
          const endTime = performance.now();
          const duration = endTime - startTime;
          
          // Position 1: Authentication error
          if (apiError.status === 401) {
            handleAuthError();
            setLoadingStates(prev => ({ 
              ...prev, 
              main: false, 
              gpu: false, 
              location: false, 
              gpuCount: false, 
              summary: false 
            }));
            return;
          }
          
          // Position 2: Check error code and use interceptor's message
          if (apiError.code) {
            const errorMessage = apiError.error || ERROR_MESSAGES[apiError.code] || ERROR_MESSAGES['FLAVORS_FETCH_FAILED'];
            setError(errorMessage);
            notification.error(errorMessage);
            logError('API data fetch failed', { 
              code: apiError.code,
              duration: `${duration.toFixed(2)}ms`,
              operation: 'getBarrackSpecs/getBarrackStocks'
            }, apiError);
            setLoadingStates(prev => ({ 
              ...prev, 
              main: false, 
              gpu: false, 
              location: false, 
              gpuCount: false, 
              summary: false 
            }));
            return;
          }
          
          // Position 3: Fallback
          const errorMessage = apiError.error || ERROR_MESSAGES['FLAVORS_FETCH_FAILED'];
          setError(errorMessage);
          notification.error(errorMessage);
          
          logError('API data fetch failed', {
            duration: `${duration.toFixed(2)}ms`,
            operation: 'getBarrackSpecs/getBarrackStocks'
          }, apiError);
          
          setLoadingStates(prev => ({ 
            ...prev, 
            main: false, 
            gpu: false, 
            location: false, 
            gpuCount: false, 
            summary: false 
          }));
        }
      }
      
      const endTime = performance.now();
      const totalDuration = endTime - startTime;
      
      logPerformance('fetch_data_complete', totalDuration, {
        success: !error,
        initialLoad: !initialDataLoaded,
        dataSource: (flavors && stocks) ? 'websocket' : 'api'
      });
      
   } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401) {
        handleAuthError();
        setLoadingStates(prev => ({ 
          ...prev, 
          main: false, 
          gpu: false, 
          location: false, 
          gpuCount: false 
        }));
        return;
      }
      
      // Position 2: Check error code and use interceptor's message
      if (err.code) {
        const errorMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['FLAVORS_FETCH_FAILED'];
        setError(errorMessage);
        notification.error(errorMessage);
        logError('Data fetch failed', { 
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoadingStates(prev => ({ 
          ...prev, 
          main: false, 
          gpu: false, 
          location: false, 
          gpuCount: false 
        }));
        return;
      }
      
      // Position 3: Fallback
      const errorMessage = err.error || ERROR_MESSAGES['FLAVORS_FETCH_FAILED'];
      setError(errorMessage);
      notification.error(errorMessage);
      
      logError('Data fetch failed with exception', {
        duration: `${duration.toFixed(2)}ms`
      }, err);
      
      setLoadingStates(prev => ({ 
        ...prev, 
        main: false, 
        gpu: false, 
        location: false, 
        gpuCount: false 
      }));
    }
  };
  
  fetchData();
}, [flavors, stocks, initialDataLoaded, handleAuthError]);

const fetchSSHKeys = useCallback(async () => {
  if (!isAuthenticated) {
    setSSHKeys([]);
    setSelectedSSHKey('');
    setSSHKeysLoaded(false);
    return;
  }
  
  const startTime = performance.now();
  
  try {
    logInfo('Fetching SSH keys', { isAuthenticated });
    
    const response = await api.getSSHKeys();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (Array.isArray(response)) {
        const processedKeys = response.map(key => ({
          id: String(key.id),
          name: truncateText(key.name, 100),
          publicKey: key.publicKey || key.public_key
        }));
        
        setSSHKeys(processedKeys);
        setSSHKeysLoaded(true);
        if (processedKeys.length > 0 && !selectedSSHKey) {
          const defaultKeyId = processedKeys[0].id;
          setSelectedSSHKey(defaultKeyId);
          
          logUserAction('ssh_key_auto_selected', {
            keyId: defaultKeyId,
            keyName: processedKeys[0].name
          });
        }

        logPerformance('fetch_ssh_keys', duration, {
          success: true,
          keysCount: processedKeys.length
        });
        
        logInfo('SSH keys loaded successfully', {
          count: processedKeys.length,
          duration: `${duration.toFixed(2)}ms`
        });
        
      } else if (response.success === true && Array.isArray(response.keys)) {
        const processedKeys = response.keys.map(key => ({
          id: String(key.id),
          name: truncateText(key.name, 100),
          publicKey: key.publicKey || key.public_key
        }));
        
        setSSHKeys(processedKeys);
        setSSHKeysLoaded(true);
        if (processedKeys.length > 0 && !selectedSSHKey) {
          const defaultKeyId = processedKeys[0].id;
          setSelectedSSHKey(defaultKeyId);
          
          logUserAction('ssh_key_auto_selected', {
            keyId: defaultKeyId,
            keyName: processedKeys[0].name
          });
        }

        logPerformance('fetch_ssh_keys', duration, {
          success: true,
          keysCount: processedKeys.length
        });
        
        logInfo('SSH keys loaded successfully', {
          count: processedKeys.length,
          duration: `${duration.toFixed(2)}ms`
        });
        
      } else if (response.success === false) {
        notification.error(getSafeErrorMessage(response));
        setSSHKeys([]);
        
        logError('SSH keys fetch failed', {
          duration: `${duration.toFixed(2)}ms`,
          response: response
        });
      }
    }
 } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      logInfo('SSH keys fetch failed due to authentication', {
        duration: `${duration.toFixed(2)}ms`,
        reason: 'not_authenticated'
      });
      handleAuthError();
      return;
    }

    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['SSH_KEY_LIST_FAILED'];
      setError(errorMessage);
      setShowRetrySSHKeys(true);
      logError('SSH keys fetch error', { 
        code: error.code,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      return;
    }

    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['SSH_KEY_LIST_FAILED'];
    setError(errorMessage);
    setShowRetrySSHKeys(true);
    setSSHKeys([]);
    
    logError('SSH keys fetch error', {
      duration: `${duration.toFixed(2)}ms`
    }, error);
  }
}, [isAuthenticated, sshKeysLoaded, handleAuthError]);
  useEffect(() => {
    fetchSSHKeys();
  }, [fetchSSHKeys]);

const fetchScripts = useCallback(async () => {
  if (!isAuthenticated || !isAdvancedRegion) {
    setScripts([]);
    setSelectedScript('');
    setScriptsLoaded(false);
    return;
  }
  
  const startTime = performance.now();
  
  setScripts([]);
  setSelectedScript('');
  
  setLoadingStates(prev => ({ ...prev, scripts: true }));
  
  try {
    logInfo('Fetching startup scripts', { isAuthenticated, isAdvancedRegion });
    
    const response = await api.getScripts();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (response.success && Array.isArray(response.scripts)) {
        const processedScripts = response.scripts.map(script => ({
          id: String(script.id),
          name: truncateText(script.name, 100),
          script_preview: script.script_preview || '',
          created_at: script.created_at,
          updated_at: script.updated_at
        }));
        
        setScripts(processedScripts);
        setScriptsLoaded(true);
        
        if (processedScripts.length > 0 && !selectedScript) {
          const defaultScriptId = processedScripts[0].id;
          setSelectedScript(defaultScriptId);
          
          logUserAction('script_auto_selected', {
            scriptId: defaultScriptId,
            scriptName: processedScripts[0].name
          });
        }

        logPerformance('fetch_scripts', duration, {
          success: true,
          scriptsCount: processedScripts.length
        });
        
        logInfo('Startup scripts loaded successfully', {
          count: processedScripts.length,
          duration: `${duration.toFixed(2)}ms`
        });
        
      } else if (response.success === false) {
        notification.error(getSafeErrorMessage(response));
        setScripts([]);
        
        logError('Scripts fetch failed', {
          duration: `${duration.toFixed(2)}ms`,
          response: response
        });
      }
    }
 } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      logInfo('Scripts fetch failed due to authentication', {
        duration: `${duration.toFixed(2)}ms`,
        reason: 'not_authenticated'
      });
      handleAuthError();
      return;
    }

    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['SCRIPT_LIST_FAILED'];
      setError(errorMessage);
      setShowRetryScripts(true);
      logError('Scripts fetch error', { 
        code: error.code,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      return;
    }

    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['SCRIPT_LIST_FAILED'];
    setError(errorMessage);
    setShowRetryScripts(true);
    setScripts([]);
    
    logError('Scripts fetch error', {
      duration: `${duration.toFixed(2)}ms`
    }, error);
  } finally {
    setLoadingStates(prev => ({ ...prev, scripts: false }));
  }
}, [isAuthenticated, isAdvancedRegion, scriptsLoaded, handleAuthError]);


  useEffect(() => {
    fetchScripts();
  }, [fetchScripts]);
const fetchVolumes = useCallback(async () => {
  if (!isAuthenticated || !selectedRegion) return;
  
  if (isAdvancedRegion) return;
  
  const startTime = performance.now();
  
  setVolumes([]);
  
  try {
    logInfo('Fetching volumes for region', {
      selectedRegion,
      isAuthenticated 
    });
    
    const response = await api.getVolumes({ 
      region: selectedRegion,
      bootable: true,
      status: 'detached'
    });
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (response.success && Array.isArray(response.volumes)) {
        setVolumes(response.volumes);
        
        logPerformance('fetch_volumes', duration, {
          success: true,
          volumesCount: response.volumes.length,
          region: selectedRegion
        });
        
        logInfo('Volumes loaded successfully', {
          count: response.volumes.length,
          region: selectedRegion,
          duration: `${duration.toFixed(2)}ms`
        });
        
      } else if (response.success === false) {
        notification.error(getSafeErrorMessage(response));
        setVolumes([]);
        
        logError('Volumes fetch failed', {
          duration: `${duration.toFixed(2)}ms`,
          region: selectedRegion,
          response: response
        });
      }
    }
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['VOLUME_LIST_FAILED'];
      setError(errorMessage);
      setShowRetryVolumes(true);
      logError('Volumes fetch error', { 
        code: error.code,
        duration: `${duration.toFixed(2)}ms`,
        region: selectedRegion
      }, error);
      return;
    }
    
    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['VOLUME_LIST_FAILED'];
    setError(errorMessage);
    setShowRetryVolumes(true);
    setVolumes([]);
    
    logError('Volumes fetch error', {
      duration: `${duration.toFixed(2)}ms`,
      region: selectedRegion
    }, error);
  }
}, [isAuthenticated, selectedRegion, isAdvancedRegion, handleAuthError]);
  useEffect(() => {
    fetchVolumes();
  }, [fetchVolumes]);

  const sortedVolumes = useMemo(() => {
    if (!Array.isArray(volumes)) {
      return { bootable: [], nonBootable: [] };
    }

    const filteredVolumes = selectedRegion 
      ? volumes.filter(vol => {
          if (!vol || !vol.region) return false;
          return vol.region.toLowerCase() === selectedRegion.toLowerCase() ||
                 vol.region.replace(/\s+/g, '-').toLowerCase() === selectedRegion.toLowerCase();
        })
      : volumes;

    const bootable = filteredVolumes.filter(vol => vol.bootable === true);
    const nonBootable = filteredVolumes.filter(vol => vol.bootable !== true);

    logInfo('Volumes sorted by bootable status', {
      totalVolumes: volumes.length,
      filteredVolumes: filteredVolumes.length,
      bootableCount: bootable.length,
      nonBootableCount: nonBootable.length,
      selectedRegion
    });

    return { bootable, nonBootable };
  }, [volumes, selectedRegion]);

  useEffect(() => {
    setBootableVolumes(sortedVolumes.bootable);
    setNonBootableVolumes(sortedVolumes.nonBootable);
  }, [sortedVolumes]);

  const filteredRegionsByGPU = useMemo(() => {
    if (
      !selectedGPU ||
      !stocksData ||
      !Array.isArray(stocksData) ||
      stocksData.length === 0 ||
      !gpuTypes
    ) {
      return [];
    }
    
    try {
      const selectedGpuObj = gpuTypes.find(gpu => gpu.id === selectedGPU);
      if (!selectedGpuObj) {
        return [];
      }
      
      const gpuTypeValue = selectedGpuObj.value;
      if (!gpuTypeValue) return [];
      
      const ADVANCED_GPUS = ['H100-80GB', 'H100-SXM5-80GB', 'H200-141GB', 'B200-180GB', 'B300-262GB'];
      const isSelectedAdvanced = ADVANCED_GPUS.includes(gpuTypeValue) || 
                                  gpuTypeValue.includes('SXM') || 
                                  gpuTypeValue.includes('H200') || 
                                  gpuTypeValue.includes('B200') || 
                                  gpuTypeValue.includes('B300');
      
      const selectedBase = gpuTypeValue.split('-')[0].toUpperCase();
      
      const availableRegions = [];
      const seenRegionIds = new Set();
      
      stocksData.forEach(regionStock => {
        if (!regionStock || !regionStock.region) return;
        
        const regionName = regionStock.region;
        const regionId = regionName.toLowerCase().replace(/\s+/g, '-');
        
        if (seenRegionIds.has(regionId)) return;
        
        const isAdvancedRegion = regionName.toLowerCase().includes('finland');
        
        if (isSelectedAdvanced && !isAdvancedRegion) return;
        if (!isSelectedAdvanced && isAdvancedRegion) return;
        
        if (!regionStock.models || !Array.isArray(regionStock.models)) return;
        
        const matchingModel = regionStock.models.find(model => {
          if (!model || !model.model) return false;
          
          const modelName = model.model;
          const isAdvancedFormat = /-(1|2|4|8)x$/.test(modelName);
          
          if (isAdvancedFormat) {
            const stockBase = modelName.split('-')[0].toUpperCase();
            return stockBase === selectedBase;
          } else {
            return modelName === gpuTypeValue;
          }
        });
        
        if (!matchingModel) return;
        
        let hasAvailable = false;
        if (matchingModel.configurations) {
          hasAvailable = Object.values(matchingModel.configurations).some(status => 
            status === 'available' || (typeof status === 'number' && status > 0)
          );
        }
        if (matchingModel.available === 'available') {
          hasAvailable = true;
        }
        
        if (hasAvailable) {
          seenRegionIds.add(regionId);
          availableRegions.push({
            id: regionId,
            name: regionName,
            value: regionName
          });
        }
      });
      
      availableRegions.sort((a, b) => {
        const aIsLegacy = a.id.includes('canada');
        const bIsLegacy = b.id.includes('canada');
        if (aIsLegacy && !bIsLegacy) return -1;
        if (!aIsLegacy && bIsLegacy) return 1;
        return a.name.localeCompare(b.name);
      });
      
      logInfo('Regions filtered from stocks only', {
        selectedGPU: selectedGpuObj.label,
        gpuTypeValue,
        isSelectedAdvanced,
        stocksDataLength: stocksData.length,
        availableRegions: availableRegions.map(r => r.name)
      });
      
      return availableRegions;
      
    } catch (error) {
      logError('Error filtering regions by GPU', {
        selectedGPU,
        stocksDataLength: stocksData?.length
      }, error);
      return [];
    }
  }, [selectedGPU, stocksData, gpuTypes]);

  useEffect(() => {
    setRegions(filteredRegionsByGPU);
    if (filteredRegionsByGPU.length > 0) {
      const regionExists = filteredRegionsByGPU.some(region => region.id === selectedRegion);
      if (!regionExists) {
        const newRegion = filteredRegionsByGPU[0].id;
        setSelectedRegion(newRegion);
        
        logUserAction('region_updated_by_gpu_filter', {
          newRegion,
          regionName: filteredRegionsByGPU[0].name,
          selectedGPU
        });
      }
    } else {
      setSelectedRegion('');
      setGpuCounts([]);
      
      logInfo('No regions available for selected GPU', { selectedGPU });
    }
    
    if (filteredRegionsByGPU.length === 0) {
      setSelectedRegion('');
      setGpuCounts([]);
      setConfiguration({});
      setLoadingStates(prev => ({ ...prev, summary: false }));
    }
  }, [filteredRegionsByGPU, selectedRegion, selectedGPU]);

  const availableGpuCounts = useMemo(() => {
    if (
      !initialDataLoaded ||
      !selectedGPU ||
      !selectedRegion ||
      !stocksData ||
      !Array.isArray(stocksData) ||
      stocksData.length === 0 ||
      !gpuTypes ||
      !regions
    ) {
      return [];
    }
    
    try {
      const selectedGpuObj = gpuTypes.find(gpu => gpu.id === selectedGPU);
      const gpuTypeValue = selectedGpuObj ? selectedGpuObj.value : '';
      
      const selectedRegionObj = regions.find(region => region.id === selectedRegion);
      const regionValue = selectedRegionObj ? selectedRegionObj.value : '';
      
      if (!gpuTypeValue || !regionValue) return [];
      
      const ADVANCED_GPUS = ['H100-80GB', 'H100-SXM5-80GB', 'H200-141GB', 'B200-180GB', 'B300-262GB'];
      const isSelectedAdvanced = ADVANCED_GPUS.includes(gpuTypeValue) || 
                                  gpuTypeValue.includes('SXM') || 
                                  gpuTypeValue.includes('H200') || 
                                  gpuTypeValue.includes('B200') || 
                                  gpuTypeValue.includes('B300');
      
      const selectedBase = gpuTypeValue.split('-')[0].toUpperCase();
      
      const availableCounts = [];
      
      const regionStock = stocksData.find(r => {
        if (!r || !r.region) return false;
        const stockRegion = r.region.toLowerCase().replace(/\s+/g, '-');
        const targetRegion = regionValue.toLowerCase().replace(/\s+/g, '-');
        return stockRegion === targetRegion;
      });
      
      if (!regionStock || !regionStock.models || !Array.isArray(regionStock.models)) {
        logInfo('No stock data found for region', { regionValue });
        return [];
      }
      
      regionStock.models.forEach(model => {
        if (!model || !model.model) return;
        
        const modelName = model.model;
        const isAdvancedFormat = /-(1|2|4|8)x$/.test(modelName);
        
        let isMatch = false;
        
        if (isAdvancedFormat) {
          const stockBase = modelName.split('-')[0].toUpperCase();
          isMatch = isSelectedAdvanced && stockBase === selectedBase;
        } else {
          isMatch = !isSelectedAdvanced && modelName === gpuTypeValue;
        }
        
        if (!isMatch) return;
        
        if (model.configurations) {
          if (!Array.isArray(model.configurations)) {
            Object.entries(model.configurations).forEach(([configKey, status]) => {
              const isAvailable = status === 'available' || 
                                  status === 'in_stock' || 
                                  (typeof status === 'number' && status > 0);
              
              if (isAvailable) {
                const countValue = parseInt(configKey.replace('x', ''), 10);
                if (!isNaN(countValue) && !availableCounts.some(c => c.value === countValue)) {
                  availableCounts.push({ value: countValue });
                }
              }
            });
          } else {
            model.configurations.forEach(config => {
              const isAvailable = config.status === 'available' || config.status === 'in_stock';
              
              if (isAvailable && config.gpu_count) {
                const countValue = parseInt(config.gpu_count, 10);
                if (!isNaN(countValue) && !availableCounts.some(c => c.value === countValue)) {
                  availableCounts.push({ value: countValue });
                }
              }
            });
          }
        }
        
        if (isAdvancedFormat && availableCounts.length === 0) {
          const countMatch = modelName.match(/-(\d+)x$/);
          if (countMatch) {
            const countValue = parseInt(countMatch[1], 10);
            const isAvailable = model.available === 'available' || 
                                model.available === 'in_stock';
            
            if (isAvailable && !availableCounts.some(c => c.value === countValue)) {
              availableCounts.push({ value: countValue });
            }
          }
        }
      });
      
      availableCounts.sort((a, b) => a.value - b.value);
      
      logInfo('GPU counts filtered by stock availability', {
        selectedGPU: selectedGpuObj?.label,
        selectedRegion: selectedRegionObj?.name,
        gpuTypeValue,
        regionValue,
        isSelectedAdvanced,
        availableCounts: availableCounts.map(c => c.value)
      });
      
      return availableCounts;
      
    } catch (error) {
      logError('Error calculating GPU counts', {
        selectedGPU,
        selectedRegion,
        stocksDataLength: stocksData?.length
      }, error);
      return [];
    }
  }, [selectedGPU, selectedRegion, stocksData, gpuTypes, regions, initialDataLoaded]);

  useEffect(() => {
    setGpuCounts(availableGpuCounts);
    
    if (availableGpuCounts.length > 0) {
      if (!userHasSelectedCount && selectedCount === 0) {
        const newCount = availableGpuCounts[0].value;
        setSelectedCount(newCount);
      }
      else if (userHasSelectedCount && selectedCount !== 0) {
        const currentIsValid = availableGpuCounts.some(c => c.value === selectedCount);
        if (!currentIsValid) {
          const newCount = availableGpuCounts[0].value;
          setSelectedCount(newCount);
          setUserHasSelectedCount(false);
        }
      }
    }
  }, [availableGpuCounts, selectedCount, userHasSelectedCount]);

  useEffect(() => {
    if (userHasSelectedCount && availableGpuCounts.length > 0) {
      const currentIsValid = availableGpuCounts.some(c => c.value === selectedCount);
      if (!currentIsValid) {
        const newCount = availableGpuCounts[0].value;
        setSelectedCount(newCount);
        setUserHasSelectedCount(false);
      }
    }
  }, [selectedGPU, selectedRegion, availableGpuCounts, selectedCount, userHasSelectedCount]);

  const fetchImagesForRegion = useCallback(async () => {
  if (!isAuthenticated) {
    return;
  }
  
  if (!selectedRegion) {
    return;
  }
  
  const isAdvancedRegionSelected = selectedRegion.toLowerCase().includes('finland');
  const isAdvancedGpuSelected = selectedGPU.toLowerCase().includes('b200') || 
                                 selectedGPU.toLowerCase().includes('b300') || 
                                 selectedGPU.toLowerCase().includes('h200') ||
                                 selectedGPU.toLowerCase().includes('h100-sxm') ||
                                 selectedGPU.toLowerCase().includes('h100-80gb');

  if (isAdvancedRegionSelected !== isAdvancedGpuSelected) {
    logInfo('Skipping image fetch - tier mismatch, waiting for state sync', {
      selectedRegion,
      selectedGPU,
      isAdvancedRegionSelected,
      isAdvancedGpuSelected
    });
    return;
  }
  

  
  const startTime = performance.now();
  try {
    const selectedRegionObj = regions.find(region => region.id === selectedRegion);
    let regionValue = selectedRegionObj ? selectedRegionObj.value : '';

    regionValue = regionValue.replace(/\s+/g, '-');

    const selectedGpuObj = gpuTypes.find(gpu => gpu.id === selectedGPU);
    const gpuTypeValue = selectedGpuObj ? selectedGpuObj.value : '';

    const LEGACY_REGIONS = ['CANADA-1'];
    const ADVANCED_REGIONS = ['Finland-1', 'Finland-2', 'Finland-3'];
    const LEGACY_GPUS = ['H100-80G-PCIe', 'H100-80G-PCIe-NVLink', 'A100-80G-PCIe', 'A100-80G-PCIe-NVLink', 'RTX-A6000', 'L40', 'A40'];
    const ADVANCED_GPUS = ['B300-262GB', 'B200-180GB', 'H200-141GB', 'H100-80GB'];

    const isLegacyRegion = LEGACY_REGIONS.includes(regionValue);
    const isAdvancedRegionCheck = ADVANCED_REGIONS.includes(regionValue);
    const isLegacyGPU = LEGACY_GPUS.includes(gpuTypeValue);
    const isAdvancedGPU = ADVANCED_GPUS.includes(gpuTypeValue);

    if ((isLegacyRegion && isAdvancedGPU) || (isAdvancedRegionCheck && isLegacyGPU)) {
      return;
    }

    logInfo('Fetching OS images for region', { 
      selectedRegion,
      regionValue 
    });

    const gpuTypeForAPI = mapGpuTypeForAPI(gpuTypeValue);

    const imagesResponse = await api.getImages(regionValue, gpuTypeForAPI);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    let processedOSVersions = [];
    
    if (imagesResponse && imagesResponse.success && Array.isArray(imagesResponse.images)) {
      imagesResponse.images.forEach((imageData) => {
        if (imageData.available_regions && Array.isArray(imageData.available_regions)) {
          const isAvailableInRegion = imageData.available_regions.some(
            availableRegion => availableRegion.toLowerCase() === regionValue.toLowerCase()
          );
          
          if (isAvailableInRegion) {
            processedOSVersions.push({
              id: imageData.image_type,
              name: truncateText(imageData.name, 100),
              image_type: imageData.image_type,
              available_regions: imageData.available_regions,
              tier: imageData.tier,
              note: imageData.note,
              is_user_volume: false
            });
          }
        }
      });
      
      if (isAdvancedRegion) {
        try {
          const volumesResponse = await api.getVolumes({ 
            region: regionValue,
            bootable: true,
            exclude_os_volumes: false,
            status: 'detached'
          });
          
          if (volumesResponse && volumesResponse.success && Array.isArray(volumesResponse.volumes)) {
            const osVolumes = volumesResponse.volumes.filter(vol => 
              vol.is_os_volume === true && 
              vol.bootable === true &&
              (vol.status === 'detached' || vol.status === 'available')
            );
            
            osVolumes.forEach(volume => {
              processedOSVersions.push({
                id: `volume_${volume.id}`,
                name: `${volume.name} (${volume.size}GB)`,
                image_type: `volume_${volume.id}`,
                available_regions: [regionValue],
                tier: 'advanced',
                note: `User Volume - ${volume.volume_type || 'HDD'}`,
                is_user_volume: true,
                volume_id: volume.id,
                volume_size: volume.size,
                volume_type: volume.volume_type,
                ssh_key_ids: volume.ssh_key_ids || []
              });
            });
          }
   } catch (volumeError) {
  logError('Error fetching user volumes for advanced region', {
    region: regionValue
  }, volumeError);
}
      }
      
      setOsVersions(processedOSVersions);
      
      if (processedOSVersions.length > 0 && 
          (!selectedOS || !processedOSVersions.some(os => os.id === selectedOS))) {
        const defaultOS = String(processedOSVersions[0].id);
        setSelectedOS(defaultOS);
        
        logUserAction('os_auto_selected', {
          osId: defaultOS,
          osName: processedOSVersions[0].name,
          region: regionValue
        });
      }

      logPerformance('fetch_images', duration, {
        success: true,
        imagesCount: processedOSVersions.length,
        region: regionValue
      });
      
      logInfo('OS images loaded successfully', {
        count: processedOSVersions.length,
        region: regionValue,
        duration: `${duration.toFixed(2)}ms`
      });
      
    } else {
      setOsVersions([]);
      setSelectedOS('');
      
      logError('No images found for region', {
        region: regionValue,
        duration: `${duration.toFixed(2)}ms`
      });
    }
 } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['IMAGE_LIST_FAILED'];
      setError(errorMessage);
      setShowRetryImages(true);
      logError('Failed to fetch OS images', { 
        code: error.code,
        duration: `${duration.toFixed(2)}ms`,
        selectedRegion
      }, error);
      return;
    }
    
    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['IMAGE_LIST_FAILED'];
    setError(errorMessage);
    setShowRetryImages(true);
    setOsVersions([]);
    setSelectedOS('');
    
    logError('Failed to fetch OS images', {
      selectedRegion,
      duration: `${duration.toFixed(2)}ms`
    }, error);
  }
}, [selectedRegion, selectedGPU, isAuthenticated, isAdvancedRegion, handleAuthError, regions, gpuTypes]);

  useEffect(() => {
    fetchImagesForRegion();
  }, [fetchImagesForRegion]);

  useEffect(() => {
    if (selectedBootSource === 'volumes' && selectedVolume) {
      const isBootable = bootableVolumes.some(vol => vol.id === selectedVolume);
      if (isBootable && selectedOS) {
        setSelectedOS('');
        
        logUserAction('os_deselected_bootable_volume', {
          selectedVolume,
          previousOS: selectedOS
        });
      }
    }
  }, [selectedBootSource, selectedVolume, bootableVolumes, selectedOS]);

  useEffect(() => {
    if (!isAdvancedRegion || !isAuthenticated || !selectedSSHKey) return;
    
    const isVolumeBoot = (selectedBootSource === 'volumes' && selectedVolume) || 
                         (osVersions.find(os => os.id === selectedOS)?.is_user_volume);
    
    if (isVolumeBoot) {
      setSelectedSSHKey('');
      
      logUserAction('ssh_key_auto_cleared', {
        reason: 'volume_boot_incompatible',
        previousKeyId: selectedSSHKey,
        bootSource: selectedBootSource
      });
    }
  }, [isAdvancedRegion, isAuthenticated, selectedBootSource, selectedVolume, selectedOS, osVersions, selectedSSHKey]);

  const configValues = useMemo(() => {
    if (loadingStates.main || !initialDataLoaded || !selectedGPU || !selectedRegion || 
        regions.length === 0 || !gpuTypes || !flavorsData) {
      return {
        configObj: {},
        priceValue: '€0.00/minute',
        priceCurrencyValue: 'USD',
        gpuTypeValue: '',
        regionValue: '',
        flavorDetails: null,
        selectedGpuObj: null,
        selectedRegionObj: null
      };
    }
    
    const selectedGpuObj = gpuTypes.find(gpu => gpu.id === selectedGPU);
    const gpuTypeValue = selectedGpuObj ? selectedGpuObj.value : '';
    
    const selectedRegionObj = regions.find(region => region.id === selectedRegion);
    const regionValue = selectedRegionObj ? selectedRegionObj.value : '';
    
    if (!gpuTypeValue || !regionValue || !Array.isArray(flavorsData)) {
      return {
        configObj: {},
        priceValue: '€0.00/minute',
        priceCurrencyValue: 'USD',
        gpuTypeValue: '',
        regionValue: '',
        flavorDetails: null,
        selectedGpuObj: null,
        selectedRegionObj: null
      };
    }
    
    const flavorDetails = dataUtils.getFlavorDetails(
      flavorsData,
      gpuTypeValue,
      regionValue,
      selectedCount
    );
    
    const configObj = {
      'GPU': selectedGpuObj ? `${selectedCount}x ${selectedGpuObj.label}` : 'N/A',
      'CPU': flavorDetails ? `${flavorDetails.cpu} vCPUs` : 'N/A',
      'Memory': flavorDetails ? `${flavorDetails.ram}GB` : 'N/A',
      'Storage': isAdvancedRegion ? `${osVolumeSize}GB` : (flavorDetails ? `${flavorDetails.total_storage}GB` : 'N/A'),
      'Location': selectedRegionObj ? selectedRegionObj.name : 'N/A',
    };
    
    const priceData = dataUtils.calculatePrice(
      flavorsData,
      gpuTypeValue,
      regionValue,
      selectedCount,
      'minute',
      userCurrency
    );

    logInfo('Configuration values calculated', {
      gpu: configObj['GPU'],
      location: configObj['Location'],
      price: priceData.price,
      currency: priceData.currency
    });
    
    return {
      configObj,
      priceValue: priceData.price,
      priceCurrencyValue: priceData.currency,
      gpuTypeValue,
      regionValue,
      flavorDetails,
      selectedGpuObj,
      selectedRegionObj
    };
  }, [loadingStates.main, initialDataLoaded, selectedGPU, selectedRegion, regions, 
      gpuTypes, flavorsData, selectedCount, userCurrency, isAdvancedRegion, osVolumeSize]);
      
  const getRatesForCurrency = (currency) => {
    const currencyRates = {
      'USD': { nvme: 0.30, hdd: 0.08, legacy: 0.1 },
      'EUR': { nvme: 0.35, hdd: 0.1, legacy: 0.1 },
      'INR': { nvme: 30.0, hdd: 8.0, legacy: 10 }
    };
    return currencyRates[currency] || currencyRates['USD'];
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'USD': '$', 'EUR': '€', 'INR': '₹' };
    return symbols[currency] || '$';
  };

      const totalHourlyCost = useMemo(() => {
    const priceMatch = price.match(/[\d.]+/);
    const pricePerMinute = priceMatch ? parseFloat(priceMatch[0]) : 0;
    const gpuCostPerHour = pricePerMinute * 60;

    let storageCost = 0;
    const rates = getRatesForCurrency(userCurrency);

    const isVolumeBoot = selectedBootSource === 'volumes' && selectedVolume;
    const isAdvancedVolumeBoot = isAdvancedRegion &&
      (isVolumeBoot || osVersions.find(os => os.id === selectedOS)?.is_user_volume);

    if (!isAdvancedRegion) {
      if (!isVolumeBoot) {
        storageCost = (100 / 1024) * rates.legacy;
      }
    } else {
      if (!isAdvancedVolumeBoot) {
        const osSize = osVolumeSize || 100;
        storageCost += (osSize / 1024) * rates.nvme;
      }
      if (newVolumes && newVolumes.length > 0) {
        newVolumes.forEach(vol => {
          const rate = vol.type === 'NVMe' ? rates.nvme : rates.hdd;
          storageCost += (vol.size / 1024) * rate;
        });
      }
    }

    return gpuCostPerHour + storageCost;
  }, [price, userCurrency, isAdvancedRegion, selectedBootSource, selectedVolume, osVersions, selectedOS, osVolumeSize, newVolumes]);

  const insufficientBalance = useMemo(() => {
    if (!isAuthenticated || !balanceLoaded || creditBalance === null) return false;
    const currencyKey = userCurrency.toLowerCase();
    const currentBalance = creditBalance[currencyKey] || 0;
    return currentBalance < totalHourlyCost;
  }, [isAuthenticated, balanceLoaded, creditBalance, userCurrency, totalHourlyCost]);

  const deployButtonTitle = useMemo(() => {
    if (!isAuthenticated) return 'Sign up to deploy';
    if (!isProfileComplete) return 'Complete your profile to deploy';
    if (insufficientBalance && balanceLoaded) {
      const currencyKey = userCurrency.toLowerCase();
      const currentBalance = creditBalance?.[currencyKey] || 0;
      const sym = getCurrencySymbol(userCurrency);
      return `Insufficient balance: ${sym}${currentBalance.toFixed(2)} available, need ${sym}${totalHourlyCost.toFixed(4)}/hr minimum`;
    }
    if (!deployEnabled) return 'Please complete all required fields';
    return 'Deploy VM';
  }, [isAuthenticated, isProfileComplete, insufficientBalance, balanceLoaded, userCurrency, creditBalance, totalHourlyCost, deployEnabled]);
  const clearError = useCallback(() => {
    setError(null);
    setLastShownError('');
  }, []);

  useEffect(() => {
    if (loadingStates.main || !initialDataLoaded) return;
    
    setLoadingStates(prev => ({ ...prev, summary: true }));
    
    try {
      setConfiguration(configValues.configObj);
      setPrice(configValues.priceValue);
      setPriceCurrency(configValues.priceCurrencyValue);
      
      const { gpuTypeValue, regionValue, flavorDetails } = configValues;
      
      const isAvailable = stocksData && Array.isArray(stocksData) ? 
        dataUtils.checkAvailability(
          stocksData,
          gpuTypeValue,
          regionValue,
          selectedCount
        ) : false;
      
      let bootSourceValid = false;

      if (isAdvancedRegion) {
        bootSourceValid = !!selectedOS;
      } else {
        bootSourceValid = 
          (selectedBootSource === 'os_images' && selectedOS) || 
          (selectedBootSource === 'volumes' && selectedVolume && bootableVolumes.some(vol => vol.id === selectedVolume)) ||
          (selectedBootSource === 'custom_images' && selectedCustomImage);
      }
      
      let sshKeyValid = true;
      if (isAuthenticated) {
        if (isAdvancedRegion) {
          const isVolumeBoot = (selectedBootSource === 'volumes' && selectedVolume) || 
                               (osVersions.find(os => os.id === selectedOS)?.is_user_volume);
          
          if (isVolumeBoot) {
            sshKeyValid = !selectedSSHKey;
          } else {
            sshKeyValid = !!selectedSSHKey;
          }
        } else {
          sshKeyValid = !!selectedSSHKey;
        }
      }      
      
      const newDeployEnabled = isAvailable && flavorDetails && sshKeyValid && bootSourceValid;
      
      setDeployEnabled(newDeployEnabled);
      
      logInfo('Deploy validation updated', {
        isAvailable,
        hasFlavorDetails: !!flavorDetails,
        sshKeyValid,
        bootSourceValid,
        deployEnabled: newDeployEnabled,
        isAuthenticated,
        isAdvancedRegion
      });
      
      setLoadingStates(prev => ({ ...prev, summary: false }));
    } catch (error) {
      setLoadingStates(prev => ({ ...prev, summary: false }));
      setConfiguration({});
      
      logError('Error updating configuration', {
        loadingStates,
        initialDataLoaded
      }, error);
    }
  }, [
    configValues,
    loadingStates.main,
    initialDataLoaded,
    stocksData,
    selectedCount,
    selectedBootSource,
    selectedOS,
    selectedVolume,
    bootableVolumes,
    selectedCustomImage,
    selectedSSHKey,
    selectedScript,
    isAuthenticated,
    isAdvancedRegion,
    osVersions
  ]);

  const validateDeployParams = useCallback(() => {
    const validationErrors = [];
    
    if (!selectedGPU) {
      validationErrors.push("GPU selection is required");
    }
    
    if (!selectedRegion) {
      validationErrors.push("Region selection is required");
    }
    
    if (selectedBootSource === 'os_images' && !selectedOS) {
      validationErrors.push("OS selection is required");
    }
    
    if (!isAdvancedRegion && selectedBootSource === 'volumes' && !selectedVolume) {
      validationErrors.push("Volume selection is required. Please select a volume.");
    }
    
    if (selectedBootSource === 'custom_images' && !selectedCustomImage) {
      validationErrors.push("Custom image selection is required");
    }
    
    if (selectedBootSource === 'volumes' && selectedVolume && !selectedOS) {
      const isBootable = bootableVolumes.some(vol => vol.id === selectedVolume);
      if (!isBootable) {
        validationErrors.push("Selected volume is not bootable. Please select an OS image or a bootable volume.");
      }
    }
    
    if (isAuthenticated && !selectedSSHKey) {
      validationErrors.push("SSH key is required");
    }
    
    if (!isAdvancedRegion && cloudInitScript) {
      const validation = validateCloudInitScript(cloudInitScript);
      if (!validation.valid) {
        validationErrors.push(validation.error);
      }
    }

    logInfo('Deploy parameters validated', {
      validationErrors,
      selectedGPU: !!selectedGPU,
      selectedRegion: !!selectedRegion,
      selectedBootSource,
      hasSelectedOS: !!selectedOS,
      hasSelectedVolume: !!selectedVolume,
      hasSelectedCustomImage: !!selectedCustomImage,
      hasSelectedSSHKey: !!selectedSSHKey,
      hasSelectedScript: !!selectedScript,
      hasCloudInitScript: !!cloudInitScript,
      publicIP,
      isAuthenticated,
      isAdvancedRegion
    });
    
    return validationErrors;
  }, [
    selectedGPU, 
    selectedRegion, 
    selectedBootSource, 
    selectedOS, 
    selectedVolume, 
    bootableVolumes, 
    selectedCustomImage,
    selectedSSHKey, 
    selectedScript,
    cloudInitScript,
    publicIP,
    isAuthenticated,
    isAdvancedRegion
  ]);

const handleDeploy = useCallback(async (instanceName = '') => {
  if (!deployEnabled) return;
  
  if (!checkDeployRateLimit()) {
    notification.warning(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
    return;
  }
  
  const startTime = performance.now();
  
  setLoadingStates(prev => ({ ...prev, deploying: true }));
  setError(null);
  setShowRetryDeploy(false);
  
  logUserAction('deploy_vm_initiated', {
    selectedGPU,
    selectedRegion,
    selectedCount,
    selectedBootSource,
    selectedScript,
    publicIP,
    hasCloudInit: !!cloudInitScript,
    hasSecurityRules: securityRules.length > 0,
    isAdvancedRegion,
    instanceName: instanceName || 'auto-generate'
  });
  
  logInfo('VM deployment started', {
    selectedGPU,
    selectedRegion,
    selectedCount,
    selectedBootSource,
    selectedScript,
    configuration,
    isAdvancedRegion,
    instanceName
  });
  
  if (deploymentTimeoutRef.current) {
    clearTimeout(deploymentTimeoutRef.current);
  }
  
  try {
    const selectedGpuObj = gpuTypes.find(gpu => gpu.id === selectedGPU);
    const gpuValue = selectedGpuObj ? mapGpuTypeForAPI(selectedGpuObj.value) : '';
    
    const selectedRegionObj = regions.find(region => region.id === selectedRegion);
    const regionValue = selectedRegionObj ? selectedRegionObj.value : '';
    
    let bootSourceConfig = {};

    if (isAdvancedRegion) {
      const osObj = osVersions.find(os => os.id === selectedOS);
      
      if (osObj && osObj.is_user_volume) {
        bootSourceConfig = {
          volume_id: osObj.volume_id,
        };
      } else {
        const osName = osObj ? osObj.name : '';
        bootSourceConfig = {
          image_name: osName,
        };
      }
    } else {
      if (selectedBootSource === 'os_images') {
        const osObj = osVersions.find(os => os.id === selectedOS);
        const osName = osObj ? osObj.name : '';
        bootSourceConfig = {
          image_name: osName,
        };
      } else if (selectedBootSource === 'volumes' && selectedVolume) {
        bootSourceConfig = {
          boot_from_volume: true,
          volume_id: selectedVolume,
        };
      } else if (selectedBootSource === 'custom_images' && selectedCustomImage) {
        const customImage = customImages.find(img => img.id === selectedCustomImage);
        const imageName = customImage ? customImage.name : '';
        bootSourceConfig = {
          image_name: imageName,
        };
      }
    }
    
    const isAdvancedVolumeBoot = isAdvancedRegion && 
        (bootSourceConfig.volume_id || osVersions.find(os => os.id === selectedOS)?.is_user_volume);

    const sshKeyObj = !isAdvancedVolumeBoot && selectedSSHKey ? 
      sshKeys.find(key => key.id === selectedSSHKey) : null;
    const sshKeyValue = sshKeyObj ? sshKeyObj.name : '';
    
    const sanitizedCloudInit = cloudInitScript ? 
      truncateText(cloudInitScript, 16384) : null;
    
    const vmConfig = {
      gpu_model: gpuValue,
      gpu_count: selectedCount,
      region: regionValue,
      ...bootSourceConfig
    };

    if (instanceName) {
      vmConfig.instance_name = instanceName;
    }

    if (sshKeyValue) {
      vmConfig.ssh_key = sshKeyValue;
    }

    if (isAdvancedRegion) {
      const isVolumeBoot = bootSourceConfig.boot_from_volume || bootSourceConfig.volume_id;
      
      if (!isVolumeBoot) {
        vmConfig.os_volume = {
          size: osVolumeSize || 100
        };
        
        if (selectedScript) {
          vmConfig.script_id = selectedScript;
        }
      }
      
      if (newVolumes && newVolumes.length > 0) {
        vmConfig.volumes = newVolumes.map(vol => ({
          name: vol.name,
          size: vol.size,
          volume_type: vol.type
        }));
      }
      
      if (selectedAttachableVolumes && selectedAttachableVolumes.length > 0) {
        vmConfig.existing_volumes = selectedAttachableVolumes;
      }
      
    } else {
      vmConfig.public_ip = publicIP;
      
      if (securityRules && securityRules.length > 0) {
        vmConfig.security_rules = securityRules;
      }
      
      if (sanitizedCloudInit) {
        vmConfig.cloud_init = sanitizedCloudInit;
      }
    }
    
    Object.keys(vmConfig).forEach(key => {
      if (vmConfig[key] === null || vmConfig[key] === undefined) {
        delete vmConfig[key];
      }
    });

    logInfo('VM deployment config prepared', {
      regionValue,
      bootSourceConfig,
      hasScript: !!selectedScript,
      hasCloudInit: !!sanitizedCloudInit,
      hasPublicIP: publicIP,
      securityRulesCount: securityRules.length,
      isAdvancedRegion,
      instanceName
    });

    const response = await api.deployServer(vmConfig);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response.success !== false) {
      const responseData = response.data || response;
      
      const createdVmId = responseData.instance_id || 
                          responseData.vm_id || 
                          responseData.id || 
                          (responseData.instances && responseData.instances[0] && 
                           responseData.instances[0].id);
      
      if (!createdVmId) {
        throw new Error('Could not extract VM ID from response');
      }
      
      notification.success("VM deployment initiated successfully!");
      
      setConfirmDeploy(false);
      fetchCreditBalance();

      logPerformance('deploy_vm', duration, {
        success: true,
        vmId: createdVmId,
        region: regionValue,
        securityRulesCount: securityRules.length,
        hasScript: !!selectedScript,
        isAdvancedRegion,
        instanceName
      });
      
      logInfo('VM deployment initiated successfully', {
        vmId: createdVmId,
        duration: `${duration.toFixed(2)}ms`,
        region: regionValue,
        securityRulesApplied: securityRules.length,
        scriptUsed: !!selectedScript,
        isAdvancedRegion,
        instanceName
      });
      
      logUserAction('deploy_vm_success', {
        vmId: createdVmId,
        duration: `${duration.toFixed(2)}ms`,
        configuration,
        securityRulesCount: securityRules.length,
        scriptUsed: !!selectedScript,
        isAdvancedRegion,
        instanceName
      });
      
    } else {
      notification.error(getSafeErrorMessage(response));
      
      logPerformance('deploy_vm', duration, {
        success: false,
        error: getSafeErrorMessage(response)
      });
      
      logError('VM deployment failed', {
        duration: `${duration.toFixed(2)}ms`,
        response,
        vmConfig
      });
      
      logUserAction('deploy_vm_failed', {
        error: getSafeErrorMessage(response),
        duration: `${duration.toFixed(2)}ms`
      });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      setLoadingStates(prev => ({ ...prev, deploying: false }));
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (err.code) {
      const errorMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['VM_DEPLOY_FAILED'];
      notification.error(errorMessage);
      setError(errorMessage);
      setShowRetryDeploy(true);
      
      logPerformance('deploy_vm', duration, {
        success: false,
        error: errorMessage,
        errorCode: err.code
      });
      
      logError('VM deployment error', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      
      logUserAction('deploy_vm_error', {
        errorCode: err.code,
        duration: `${duration.toFixed(2)}ms`
      });
      
      setLoadingStates(prev => ({ ...prev, deploying: false }));
      return;
    }
    
    // Position 3: Fallback
    const errorMessage = err.error || ERROR_MESSAGES['VM_DEPLOY_FAILED'];
    notification.error(errorMessage);
    setError(errorMessage);
    setShowRetryDeploy(true);
    
    logPerformance('deploy_vm', duration, {
      success: false,
      error: errorMessage
    });
    
    logError('VM deployment error', {
      duration: `${duration.toFixed(2)}ms`
    }, err);
    
    logUserAction('deploy_vm_error', {
      duration: `${duration.toFixed(2)}ms`
    });
    
    setLoadingStates(prev => ({ ...prev, deploying: false }));
  } finally {
    setLoadingStates(prev => ({ ...prev, deploying: false }));
  }
}, [
  deployEnabled, 
  securityRules, 
  gpuTypes, 
  selectedGPU, 
  regions, 
  selectedRegion,
  selectedBootSource,
  osVersions,
  selectedOS,
  selectedVolume,
  selectedCustomImage,
  customImages,
  sshKeys,
  selectedSSHKey,
  selectedScript,
  selectedCount,
  cloudInitScript,
  publicIP,
  notification,
  configuration,
  isAdvancedRegion,
  checkDeployRateLimit,
  osVolumeSize,
  newVolumes,
  selectedAttachableVolumes,
handleAuthError,
  fetchCreditBalance
]);
const fetchCustomImages = useCallback(async () => {
  if (!isAuthenticated) return;
  
  const startTime = performance.now();
  
  try {
    logInfo('Fetching custom images', { isAuthenticated });
    
    const response = await api.getCustomImages();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (response.success) {
        let extractedImages = [];
        if (response.images && Array.isArray(response.images)) {
          extractedImages = response.images.map(img => ({
            ...img,
            name: truncateText(img.name, 100)
          }));
        }
        
        setCustomImages(extractedImages);
        if (extractedImages.length > 0 && !selectedCustomImage) {
          const defaultImageId = extractedImages[0].id;
          setSelectedCustomImage(defaultImageId);
          
          logUserAction('custom_image_auto_selected', {
            imageId: defaultImageId,
            imageName: extractedImages[0].name
          });
        }

        logPerformance('fetch_custom_images', duration, {
          success: true,
          imagesCount: extractedImages.length
        });
        
        logInfo('Custom images loaded successfully', {
          count: extractedImages.length,
          duration: `${duration.toFixed(2)}ms`
        });
        
      } else {
        notification.error(getSafeErrorMessage(response));
        
        logError('Custom images fetch failed', {
          duration: `${duration.toFixed(2)}ms`,
          response
        });
      }
    }
} catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (err.code) {
      const errorMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['IMAGE_LIST_FAILED'];
      notification.error(errorMessage);
      logError('Custom images fetch error', { 
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 3: Fallback
    const errorMessage = err.error || ERROR_MESSAGES['IMAGE_LIST_FAILED'];
    notification.error(errorMessage);
    
    logError('Custom images fetch error', {
      duration: `${duration.toFixed(2)}ms`
    }, err);
  }
}, [isAuthenticated, handleAuthError]);

  const handleBootSourceChange = useCallback((sourceId) => {
    setSelectedBootSource(sourceId);
    
    logUserAction('boot_source_changed', {
      previousSource: selectedBootSource,
      newSource: sourceId,
      isAuthenticated
    });
    
    logInfo('Boot source changed', {
      from: selectedBootSource,
      to: sourceId
    });
    
    if (sourceId === 'volumes') {
      // Volume fetch is handled by useEffect
    } else if (sourceId === 'custom_images') {
      fetchCustomImages();
    }
  }, [fetchCustomImages, selectedBootSource, isAuthenticated]);

  const handleDeployClick = useCallback(async () => {
    if (!isAuthenticated) {
      logUserAction('deploy_redirect_signup', {
        reason: 'not_authenticated'
      });
      navigate('/signup');
      return;
    }
    
    if (!isProfileComplete) {
      logUserAction('deploy_redirect_profile', {
        reason: 'profile_incomplete'
      });
      navigate('/account');
      return;
    }
    
    try {
      const response = await api.getCreditBalance();
      if (response && response.success && response.balance) {
        setCreditBalance(response.balance);
        setBalanceLoaded(true);
        
        const currencyKey = userCurrency.toLowerCase();
        const currentBalance = response.balance[currencyKey] || 0;
        
        if (currentBalance < totalHourlyCost) {
          const sym = getCurrencySymbol(userCurrency);
          notification.error(
            `Insufficient balance: ${sym}${currentBalance.toFixed(2)} available, need at least ${sym}${totalHourlyCost.toFixed(4)}/hr`
          );
          return;
        }
      }
    } catch (error) {
      logError('Balance check before deploy failed', {}, error);
    }
    
    setConfirmDeploy(true);
    
    logUserAction('deploy_modal_opened', {
      configuration,
      deployEnabled
    });
  }, [isAuthenticated, isProfileComplete, navigate, configuration, deployEnabled, userCurrency, totalHourlyCost]);

  const handleOpenFirewallModal = useCallback(() => {
    setFirewallModalOpen(true);
    
    logUserAction('firewall_modal_opened', {
      currentRulesCount: securityRules.length
    });
    
    logInfo('Firewall modal opened', {
      currentRulesCount: securityRules.length
    });
  }, [securityRules]);
  
  const handleFirewallSave = useCallback((rules) => {
    setSecurityRules(rules);
    
    logUserAction('firewall_rules_saved', {
      previousRulesCount: securityRules.length,
      newRulesCount: rules.length
    });
    
    logInfo('Firewall rules updated', {
      previousCount: securityRules.length,
      newCount: rules.length
    });
  }, [securityRules]);
  
  const handleImportSSHKey = useCallback(() => {
    setSSHKeyModalType('import');
    setSSHKeyModalOpen(true);
    
    logUserAction('ssh_key_import_modal_opened');
    logInfo('SSH key import modal opened');
  }, []);
  
  const handleCreateSSHKey = useCallback(() => {
    setSSHKeyModalType('create');
    setSSHKeyModalOpen(true);
    
    logUserAction('ssh_key_create_modal_opened');
    logInfo('SSH key create modal opened');
  }, []);

  const handleCreateScript = useCallback(() => {
    setScriptModalType('create');
    setEditingScript(null);
    setScriptModalOpen(true);
    
    logUserAction('script_create_modal_opened');
    logInfo('Script create modal opened');
  }, []);

  const handleEditScript = useCallback((scriptId) => {
    setScriptModalType('edit');
    const script = scripts.find(s => s.id === scriptId);
    setEditingScript(script);
    setScriptModalOpen(true);
    
    logUserAction('script_edit_modal_opened', { scriptId });
    logInfo('Script edit modal opened', { scriptId });
  }, [scripts]);

  const handleDeleteScript = useCallback(async (scriptId) => {
    const script = scripts.find(s => s.id === scriptId);

    logUserAction('delete_script_initiated', {
      scriptId,
      scriptName: script?.name || 'Unknown'
    });

    logInfo('Script deletion initiated', {
      scriptId,
      scriptName: script?.name
    });

    setScriptToDelete({ id: scriptId, name: script?.name || 'Unknown' });
    setShowDeleteConfirmation(true);
  }, [scripts]);


  const handleBulkDeleteScripts = useCallback(async (scriptIds) => {
  const startTime = performance.now();
  
  try {
    logUserAction('bulk_delete_scripts_initiated', {
      scriptsCount: scriptIds?.length || 0
    });
    
    logInfo('Bulk delete scripts initiated from DeployVM', {
      scriptsCount: scriptIds?.length || 0
    });
    
    const response = await api.deleteMultipleScripts(scriptIds);
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && response.success) {
      setScripts(prev => prev.filter(script => !scriptIds.includes(script.id)));
      
      if (scriptIds.includes(selectedScript)) {
        const remainingScripts = scripts.filter(script => !scriptIds.includes(script.id));
        setSelectedScript(remainingScripts.length > 0 ? remainingScripts[0].id : '');
      }
      
      logPerformance('bulk_delete_scripts', duration, {
        success: true,
        scriptsCount: scriptIds.length
      });
      
      logUserAction('bulk_delete_scripts_success', {
        scriptsCount: scriptIds.length,
        duration: `${duration.toFixed(2)}ms`
      });
      
      notification.success(`Successfully deleted ${scriptIds.length} script${scriptIds.length > 1 ? 's' : ''}`);
    } else {
      notification.error(getSafeErrorMessage(response));
      
      logError('Bulk delete scripts failed', {
        scriptsCount: scriptIds.length,
        duration: `${duration.toFixed(2)}ms`,
        response
      });
      
      throw new Error(getSafeErrorMessage(response));
    }
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      throw error;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['SCRIPTS_DELETE_FAILED'];
      notification.error(errorMessage);
      logError('Bulk delete scripts error', { 
        code: error.code,
        scriptsCount: scriptIds?.length || 0,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      throw error;
    }
    
    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['SCRIPTS_DELETE_FAILED'];
    notification.error(errorMessage);
    
    logError('Bulk delete scripts error', {
      scriptsCount: scriptIds?.length || 0,
      duration: `${duration.toFixed(2)}ms`
    }, error);
    
    throw error;
  }
}, [scripts, selectedScript, handleAuthError]);

const confirmDeleteScript = useCallback(async () => {
  if (!scriptToDelete) return;
  
  const startTime = performance.now();
  const scriptId = scriptToDelete.id;

  try {
    setLoadingStates(prev => ({ ...prev, deletingScript: scriptId }));
    clearError();

    const response = await api.deleteScript(scriptId);
    
    if (response && response.success) {
      setScripts(prev => prev.filter(script => script.id !== scriptId));
      
      if (selectedScript === scriptId) {
        const remainingScripts = scripts.filter(script => script.id !== scriptId);
        setSelectedScript(remainingScripts.length > 0 ? remainingScripts[0].id : '');
      }

      const endTime = performance.now();
      const duration = endTime - startTime;

      logPerformance('delete_script_ui', duration, {
        success: true,
        scriptId
      });

      logUserAction('delete_script_success', {
        scriptId,
        duration: `${duration.toFixed(2)}ms`
      });

      notification.success('Script deleted successfully');
    } else {
      notification.error(getSafeErrorMessage(response));
      
      logError('Script deletion failed', {
        scriptId,
        response
      });
    }

 } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      setLoadingStates(prev => ({ ...prev, deletingScript: false }));
      setShowDeleteConfirmation(false);
      setScriptToDelete(null);
      return;
    }

    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['SCRIPT_DELETE_FAILED'];
      notification.error(errorMessage);
      
      logPerformance('delete_script_ui', duration, {
        success: false,
        errorCode: error.code
      });

      logError('Script deletion error', {
        code: error.code,
        scriptId,
        duration: `${duration.toFixed(2)}ms`
      }, error);

      logUserAction('delete_script_error', {
        errorCode: error.code,
        scriptId
      });
      
      setLoadingStates(prev => ({ ...prev, deletingScript: false }));
      setShowDeleteConfirmation(false);
      setScriptToDelete(null);
      return;
    }

    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['SCRIPT_DELETE_FAILED'];
    notification.error(errorMessage);

    logPerformance('delete_script_ui', duration, {
      success: false
    });

    logError('Script deletion error', {
      scriptId,
      duration: `${duration.toFixed(2)}ms`
    }, error);

    logUserAction('delete_script_error', {
      scriptId
    });
    
    setLoadingStates(prev => ({ ...prev, deletingScript: false }));
    setShowDeleteConfirmation(false);
    setScriptToDelete(null);
  } finally {
    setLoadingStates(prev => ({ ...prev, deletingScript: false }));
    setShowDeleteConfirmation(false);
    setScriptToDelete(null);
  }
}, [scriptToDelete, scripts, selectedScript, notification, clearError, handleAuthError]);


const refreshScriptsFromParent = useCallback(async () => {
  if (!isAuthenticated || !isAdvancedRegion) return;
  
  const startTime = performance.now();
  
  try {
    logInfo('Manual scripts refresh initiated');
    
    const response = await api.getScripts();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (response.success && Array.isArray(response.scripts)) {
        const processedScripts = response.scripts.map(script => ({
          id: String(script.id),
          name: truncateText(script.name, 100),
          script_preview: script.script_preview || '',
          created_at: script.created_at,
          updated_at: script.updated_at
        }));
        
        setScripts(processedScripts);
        
        logPerformance('manual_scripts_refresh', duration, {
          success: true,
          scriptsCount: processedScripts.length
        });
        
        logInfo('Manual scripts refresh completed', {
          count: processedScripts.length,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    }
 } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['SCRIPT_LIST_FAILED'];
      notification.error(errorMessage);
      logError('Manual scripts refresh failed', { 
        code: error.code,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      return;
    }
    
    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['SCRIPT_LIST_FAILED'];
    notification.error(errorMessage);
    
    logError('Manual scripts refresh failed', {
      duration: `${duration.toFixed(2)}ms`
    }, error);
  }
}, [isAuthenticated, isAdvancedRegion, handleAuthError]);

const handleScriptModalSave = useCallback(async (scriptData) => {
  const startTime = performance.now();
  
  try {
    logUserAction('script_save_initiated', {
      mode: scriptModalType,
      scriptName: scriptData.name,
      hasContent: !!scriptData.script
    });
    
    let response;
    let operation;
    
    if (scriptModalType === 'create') {
      response = await api.createScript(scriptData);
      operation = 'create_script';
    } else {
      response = await api.updateScript(editingScript.id, scriptData);
      operation = 'update_script';
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && response.success) {
      const scriptInfo = response.script || scriptData;
      
      if (response.code === 'SCRIPT_UPDATED_PARTIAL') {
        logUserAction('script_update_partial', {
          oldScriptId: editingScript?.id,
          newScriptId: scriptInfo.id,
          warning: response.warning
        });
        
        notification.warning(
          response.warning || 
          'Script updated but old script may still exist. Refreshing to sync with server.'
        );
        
        setScriptModalOpen(false);
        setEditingScript(null);
        
        try {
          await refreshScriptsFromParent();
          
          if (scriptInfo.id) {
            setSelectedScript(scriptInfo.id);
          }
          
          logInfo('Scripts refreshed after partial update', {
            newScriptId: scriptInfo.id
          });
        } catch (refreshError) {
          logError('Failed to refresh scripts after partial update', {
            error: refreshError.message
          }, refreshError);
        }
        
        return;
      }
      
      if (scriptModalType === 'create') {
        const newScript = {
          id: String(scriptInfo.id || Date.now()),
          name: truncateText(scriptInfo.name, 100),
          script_preview: scriptInfo.script ? scriptInfo.script.substring(0, 100) + '...' : '',
          created_at: scriptInfo.created_at || new Date().toISOString(),
          updated_at: scriptInfo.updated_at || new Date().toISOString()
        };
        
        setScripts(prev => [...prev, newScript]);
        
        setSelectedScript(newScript.id);
        
        logUserAction('script_create_success', {
          scriptId: newScript.id,
          scriptName: newScript.name,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const newScriptId = String(scriptInfo.id || editingScript.id);
        const oldScriptId = editingScript.id;
        const wasSelected = selectedScript === oldScriptId;
        const idChanged = newScriptId !== oldScriptId;
        
        setScripts(prev => {
          const filtered = prev.filter(script => script.id !== oldScriptId);
          const updatedScript = {
            id: newScriptId,
            name: truncateText(scriptInfo.name, 100),
            script_preview: scriptInfo.script ? scriptInfo.script.substring(0, 100) + '...' : '',
            created_at: scriptInfo.created_at || new Date().toISOString(),
            updated_at: scriptInfo.updated_at || new Date().toISOString()
          };
          return [...filtered, updatedScript];
        });
        
        if (wasSelected) {
          setSelectedScript(newScriptId);
        }
        
        if (idChanged) {
          notification.info(`Script updated. Note: Backend assigned new ID to the updated script.`);
        }
        
        logUserAction('script_update_success', {
          oldScriptId: oldScriptId,
          newScriptId: newScriptId,
          idChanged: idChanged,
          scriptName: scriptInfo.name,
          duration: `${duration.toFixed(2)}ms`
        });
      }
      
      logPerformance(operation, duration, {
        success: true,
        scriptName: scriptInfo.name
      });
      
      logInfo(`Script ${scriptModalType}d successfully`, {
        scriptName: scriptInfo.name,
        duration: `${duration.toFixed(2)}ms`
      });
      
      setScriptModalOpen(false);
      setEditingScript(null);
      
      notification.success(`Script ${scriptModalType}d successfully`);
    } else {
      notification.error(getSafeErrorMessage(response));
      
      logError(`Script ${scriptModalType} failed`, {
        scriptName: scriptData.name,
        duration: `${duration.toFixed(2)}ms`,
        response
      });
    }
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    const defaultError = scriptModalType === 'create' ? ERROR_MESSAGES['SCRIPT_CREATE_FAILED'] : ERROR_MESSAGES['SCRIPT_UPDATE_FAILED'];
    
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || defaultError;
      notification.error(errorMessage);
      logError(`Script ${scriptModalType} error`, { 
        code: error.code,
        scriptName: scriptData.name,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      return;
    }
    
    // Position 3: Fallback
    const errorMessage = error.error || defaultError;
    notification.error(errorMessage);
    
    logError(`Script ${scriptModalType} error`, {
      scriptName: scriptData.name,
      duration: `${duration.toFixed(2)}ms`
    }, error);
  }
}, [scriptModalType, editingScript, notification, selectedScript, scripts, refreshScriptsFromParent, handleAuthError]);

  const handleScriptSelection = useCallback((scriptId) => {
    const previousScript = selectedScript;
    setSelectedScript(scriptId);
    
    const scriptObj = scripts.find(script => script.id === scriptId);
    
    logUserAction('script_selected', {
      previousScript,
      newScript: scriptId,
      scriptName: scriptObj?.name || 'Unknown'
    });
    
    logInfo('Script selection changed', {
      from: previousScript,
      to: scriptId,
      scriptName: scriptObj?.name
    });
  }, [selectedScript, scripts]);


const refreshSSHKeysFromParent = useCallback(async () => {
  if (!isAuthenticated) return;
  
  const startTime = performance.now();
  
  try {
    logInfo('Manual SSH keys refresh initiated by SSHKeySelector');
    
    const response = await api.getSSHKeys();
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (response && validateErrorObject(response)) {
      if (Array.isArray(response)) {
        const processedKeys = response.map(key => ({
          id: String(key.id),
          name: truncateText(key.name, 100),
          publicKey: key.publicKey || key.public_key
        }));
        
        setSSHKeys(processedKeys);
        
        logPerformance('manual_ssh_refresh', duration, {
          success: true,
          keysCount: processedKeys.length
        });
        
        logInfo('Manual SSH keys refresh completed', {
          count: processedKeys.length,
          duration: `${duration.toFixed(2)}ms`
        });
        
      } else if (response.success === true && Array.isArray(response.keys)) {
        const processedKeys = response.keys.map(key => ({
          id: String(key.id),
          name: truncateText(key.name, 100),
          publicKey: key.publicKey || key.public_key
        }));
        
        setSSHKeys(processedKeys);
        
        logPerformance('manual_ssh_refresh', duration, {
          success: true,
          keysCount: processedKeys.length
        });
      }
    }
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (error.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (error.code) {
      const errorMessage = error.error || ERROR_MESSAGES[error.code] || ERROR_MESSAGES['SSH_KEY_LIST_FAILED'];
      notification.error(errorMessage);
      logError('Manual SSH keys refresh failed', { 
        code: error.code,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      return;
    }
    
    // Position 3: Fallback
    const errorMessage = error.error || ERROR_MESSAGES['SSH_KEY_LIST_FAILED'];
    notification.error(errorMessage);
    
    logError('Manual SSH keys refresh failed', {
      duration: `${duration.toFixed(2)}ms`
    }, error);
  }
}, [isAuthenticated, handleAuthError]);

  const handleSSHKeySave = useCallback(async (newKey) => {
    const keyExists = sshKeys.some(key => key.id === newKey.id);
    if (!keyExists) {
      setSSHKeys(prevKeys => [...prevKeys, {
        ...newKey,
        name: truncateText(newKey.name, 100)
      }]);
    }
    setSelectedSSHKey(newKey.id);
    setSSHKeyModalOpen(false);
    
    logUserAction('ssh_key_saved', {
      keyId: newKey.id,
      keyName: newKey.name,
      existed: keyExists
    });
    
    logInfo('SSH key saved and selected', {
      keyId: newKey.id,
      keyName: newKey.name,
      existed: keyExists
    });
  }, [sshKeys]);

  const handleAddCloudInit = useCallback(() => {
    setCloudInitModalOpen(true);
    
    logUserAction('cloud_init_modal_opened', {
      hasExistingScript: !!cloudInitScript
    });
    
    logInfo('Cloud-init modal opened', {
      hasExistingScript: !!cloudInitScript,
      scriptLength: cloudInitScript.length
    });
  }, [cloudInitScript]);

  const handleCloudInitSave = useCallback((script) => {
    const validation = validateCloudInitScript(script);
    if (!validation.valid) {
      notification.error(validation.error);
      
      logUserAction('cloud_init_save_failed', {
        error: validation.error,
        scriptLength: script.length
      });
      return;
    }
    
    const truncatedScript = truncateText(script, 16384);
    setCloudInitScript(truncatedScript);
    setCloudInitModalOpen(false);

    logUserAction('cloud_init_saved', {
      scriptLength: truncatedScript.length,
      previousLength: cloudInitScript.length
    });

    logInfo('Cloud-init script saved', {
      scriptLength: truncatedScript.length,
      previousLength: cloudInitScript.length
    });
  }, [cloudInitScript]);

  const handleNavigateToVolumes = useCallback(() => {
    navigate('/volumes');
    
    logUserAction('navigate_to_volumes', {
      from: 'deploy_vm_page'
    });
    
    logInfo('User navigated to volumes page');
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    logUserAction('page_refresh', {
      currentPage: 'deploy_vm'
    });
    
    logInfo('User initiated page refresh');
    
    window.location.reload();
  }, []);

  const handleGPUSelection = useCallback((gpuId) => {
    clearError();
    const previousGPU = selectedGPU;
    
    const gpuObj = gpuTypes.find(gpu => gpu.id === gpuId);
    const gpuTypeValue = gpuObj?.value;
    
    if (gpuTypeValue && flavorsData && Array.isArray(flavorsData)) {
      const availableRegionNames = [];
      
      flavorsData.forEach(flavor => {
        if (flavor && flavor.gpu === gpuTypeValue && 
            flavor.available === true &&
            (flavor.region_name || flavor.region)) {
          const regionName = flavor.region_name || flavor.region;
          if (!availableRegionNames.includes(regionName)) {
            availableRegionNames.push(regionName);
          }
        }
      });
      
      const allRegions = regions.length > 0 ? regions : dataUtils.getRegionsFromFlavors(flavorsData);
      const validRegions = allRegions.filter(region =>
        region && region.value && availableRegionNames.includes(region.value)
      );
      
      if (validRegions.length > 0) {
        setSelectedRegion(validRegions[0].id);
      }
    }
    
    setSelectedGPU(gpuId);
    setUserHasSelectedCount(false);
    setSelectedCount(0);
    
    logUserAction('gpu_selected', {
      previousGPU,
      newGPU: gpuId,
      gpuType: gpuObj?.label || 'Unknown'
    });
  }, [selectedGPU, gpuTypes, regions, flavorsData, clearError]);

  const handleLocationSelection = useCallback((locationId) => {
    clearError();
    const previousRegion = selectedRegion;
    setSelectedRegion(locationId);
    setUserHasSelectedCount(false);
    
    const regionObj = regions.find(region => region.id === locationId);
    
    logUserAction('location_selected', {
      previousRegion,
      newRegion: locationId,
      regionName: regionObj?.name || 'Unknown'
    });
  }, [selectedRegion, regions, clearError]);

  const handleGPUCountSelection = useCallback((count) => {
    clearError();
    const previousCount = selectedCount;
    setSelectedCount(count);
    setUserHasSelectedCount(true);
    
    logUserAction('gpu_count_selected', {
      previousCount,
      newCount: count
    });
  }, [selectedCount, clearError]);

  const handleOSSelection = useCallback((osId) => {
    clearError();
    const previousOS = selectedOS;
    setSelectedOS(osId);
    
    const osObj = osVersions.find(os => os.id === osId);
    
    logUserAction('os_selected', {
      previousOS,
      newOS: osId,
      osName: osObj?.name || 'Unknown'
    });
    
    logInfo('OS selection changed', {
      from: previousOS,
      to: osId,
      osName: osObj?.name
    });
  }, [selectedOS, osVersions, clearError]);

  const handleVolumeSelection = useCallback((volumeId) => {
    const previousVolume = selectedVolume;
    setSelectedVolume(volumeId);
    
    const volumeObj = volumes.find(vol => vol.id === volumeId);
    
    logUserAction('volume_selected', {
      previousVolume,
      newVolume: volumeId,
      volumeName: volumeObj?.name || 'Unknown',
      isBootable: volumeObj?.bootable || false
    });
    
    logInfo('Volume selection changed', {
      from: previousVolume,
      to: volumeId,
      volumeName: volumeObj?.name,
      isBootable: volumeObj?.bootable
    });
  }, [selectedVolume, volumes]);

  const handleCustomImageSelection = useCallback((imageId) => {
    const previousImage = selectedCustomImage;
    setSelectedCustomImage(imageId);
    
    const imageObj = customImages.find(img => img.id === imageId);
    
    logUserAction('custom_image_selected', {
      previousImage,
      newImage: imageId,
      imageName: imageObj?.name || 'Unknown'
    });
    
    logInfo('Custom image selection changed', {
      from: previousImage,
      to: imageId,
      imageName: imageObj?.name
    });
  }, [selectedCustomImage, customImages]);

  const handleSSHKeySelection = useCallback((keyId) => {
    clearError();
    const previousKey = selectedSSHKey;
    setSelectedSSHKey(keyId);
    
    const keyObj = sshKeys.find(key => key.id === keyId);
    
    logUserAction('ssh_key_selected', {
      previousKey,
      newKey: keyId,
      keyName: keyObj?.name || 'Unknown'
    });
    
    logInfo('SSH key selection changed', {
      from: previousKey,
      to: keyId,
      keyName: keyObj?.name
    });
  }, [selectedSSHKey, sshKeys, clearError]);

  const handlePublicIPToggle = useCallback(() => {
    const newValue = !publicIP;
    setPublicIP(newValue);
    
    logUserAction('public_ip_toggled', {
      from: publicIP,
      to: newValue
    });
    
    logInfo('Public IP setting toggled', {
      from: publicIP,
      to: newValue
    });
  }, [publicIP]);

  const handleOsVolumeSizeChange = useCallback((size) => {
    setOsVolumeSize(size);
  }, []);

  const handleNewVolumesChange = useCallback((volumes) => {
    setNewVolumes(volumes);
  }, []);

  const handleAttachableVolumesChange = useCallback((volumes) => {
    setSelectedAttachableVolumes(volumes);
  }, []);

  useEffect(() => {
    return () => {
      if (deploymentTimeoutRef.current) {
        clearTimeout(deploymentTimeoutRef.current);
      }
      
      logInfo('DeployVM component unmounted');
    };
  }, []);


  const DeployConfirmationDialog = ({ 
    isOpen, 
    onConfirm, 
    onCancel, 
    currency,
    configuration,
    price,
    isAdvancedRegion,
    publicIP,
    selectedBootSource,
    selectedVolume,
    bootableVolumes,
    selectedCustomImage,
    customImages,
    osVersions,
    selectedOS,
    selectedScript,
    scripts,
    sshKeys,
    selectedSSHKey,
    newVolumes,
    osVolumeSize,
    selectedAttachableVolumes,
    truncateText,
    getCurrencySymbol,
    getRatesForCurrency
  }) => {
    const cleanOSName = (name) => {
      if (!name) return name;
      return name.replace(/\s*\([^)]*\)\s*$/, '').trim();
    };
    
    const truncateForDisplay = (text, maxLength = 35) => {
      if (!text || text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };
    
    const [localInstanceName, setLocalInstanceName] = useState('');
    const [showNameInput, setShowNameInput] = useState(false);
    const [nameError, setNameError] = useState('');
    const [showStorageBreakdown, setShowStorageBreakdown] = useState(false);

    useEffect(() => {
      if (isOpen) {
        setLocalInstanceName('');
        setShowNameInput(false);
        setNameError('');
        setShowStorageBreakdown(false);
      }
    }, [isOpen]);

    if (!isOpen) return null;

    const validateInstanceName = (name) => {
      if (!name) return null;
      
      if (name.length < 3) return "Minimum 3 characters required";
      if (name.length > 30) return "Maximum 30 characters allowed";
      if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
        return "Only letters, numbers, hyphens, and underscores allowed";
      }
      if (name.startsWith('-') || name.startsWith('_')) {
        return "Cannot start with hyphen or underscore";
      }
      if (name.endsWith('-') || name.endsWith('_')) {
        return "Cannot end with hyphen or underscore";
      }
      return null;
    };

    const getValidationState = () => {
      if (!showNameInput) return 'hidden';
      if (localInstanceName.length === 0) return 'empty';
      
      const error = validateInstanceName(localInstanceName);
      if (error) return 'invalid';
      return 'valid';
    };

    const validationState = getValidationState();
    const currentError = localInstanceName.length > 0 ? validateInstanceName(localInstanceName) : null;

    const isConfirmDisabled = showNameInput && localInstanceName.length > 0 && validationState === 'invalid';

    const getBorderColor = () => {
      if (!showNameInput) return 'border-[#333333]';
      if (localInstanceName.length === 0) return 'border-red-500';
      if (validationState === 'valid') return 'border-[#e0eacc]';
      return 'border-red-500';
    };

    const getCharCountColor = () => {
      if (validationState === 'valid') return 'text-[#e0eacc]';
      return 'text-red-400';
    };

    const handleConfirm = () => {
      if (isConfirmDisabled) return;
      
      const finalName = showNameInput && localInstanceName.trim() ? localInstanceName.trim() : '';
      onConfirm(finalName);
    };

    const handleNameChange = (e) => {
      const value = e.target.value;
      if (/^[a-zA-Z0-9_-]*$/.test(value)) {
        setLocalInstanceName(value);
        if (value.length === 0) {
          setNameError('');
        }
      }
    };
    
    const getLegacyDataStorage = (gpuType, count) => {
      const storagePerGpu = {
        'A100-80G-PCIe': 750,
        'H100-80G-PCIe-NVLink': 812.5,
        'H100-80G-PCIe': 750,
        'L40': 725,
        'RTX-A6000': 0
      };
      
      const perGpu = storagePerGpu[gpuType] || 0;
      return perGpu * count;
    };

    const calculateStorageDisplay = () => {
      const isLegacy = !isAdvancedRegion;
      const isVolumeBoot = selectedBootSource === 'volumes' && selectedVolume;
      const isAdvancedVolumeBoot = isAdvancedRegion && 
        (isVolumeBoot || osVersions.find(os => os.id === selectedOS)?.is_user_volume);
      
      let storageCost = 0;
      let breakdown = [];

      if (isLegacy) {
        if (!isVolumeBoot) {
          const rates = getRatesForCurrency(currency);
          const legacyOsRate = (100 / 1024) * rates.legacy;
          storageCost = legacyOsRate;
          const currencySymbol = getCurrencySymbol(currency);
          breakdown.push({ label: 'OS: 100GB SSD', cost: `${currencySymbol}${legacyOsRate.toFixed(4)}/hr` });
        }
        
        const gpuConfig = configuration['GPU'] || '';
        const gpuMatch = gpuConfig.match(/\d+x\s+(.+)/);
        const gpuLabel = gpuMatch ? gpuMatch[1] : '';
        
        const gpuTypeMap = {
          'A100 PCIe 80GB': 'A100-80G-PCIe',
          'H100 PCIe NVLink 80GB': 'H100-80G-PCIe-NVLink',
          'H100 PCIe 80GB': 'H100-80G-PCIe',
          'L40': 'L40',
          'RTX A6000': 'RTX-A6000'
        };
        
        const gpuType = gpuTypeMap[gpuLabel] || '';
        const countMatch = gpuConfig.match(/(\d+)x/);
        const gpuCount = countMatch ? parseInt(countMatch[1], 10) : 1;
        
        const dataSize = getLegacyDataStorage(gpuType, gpuCount);
        
        if (dataSize > 0) {
          breakdown.push({ 
            label: `Data: ${dataSize}GB`, 
            cost: 'Free', 
            isFree: true 
          });
        }
        
        if (isVolumeBoot) {
          breakdown.push({ 
            label: 'Using existing OS volume (already billed separately)', 
            cost: '', isNote: true 
          });
        }
      } else {
        if (!isAdvancedVolumeBoot) {
          const rates = getRatesForCurrency(currency);
          const currencySymbol = getCurrencySymbol(currency);
          const osSize = osVolumeSize || 100;
          const osCost = (osSize / 1024) * rates.nvme;
          storageCost += osCost;
          breakdown.push({ 
            label: `OS: ${osSize}GB NVMe`, 
            cost: `${currencySymbol}${osCost.toFixed(4)}/hr` 
          });
        }
        
        if (newVolumes && newVolumes.length > 0) {
          const rates = getRatesForCurrency(currency);
          const currencySymbol = getCurrencySymbol(currency);
          newVolumes.forEach(vol => {
            const rate = vol.type === 'NVMe' ? rates.nvme : rates.hdd;
            const cost = (vol.size / 1024) * rate;
            storageCost += cost;
            breakdown.push({
              label: `Data (${vol.name}): ${vol.size}GB ${vol.type}`,
              cost: `${currencySymbol}${cost.toFixed(4)}/hr`
            });
          });
        }
        
        if (isAdvancedVolumeBoot && breakdown.length === 0) {
          breakdown.push({ 
            label: 'Using existing OS volume (already billed separately)', 
            cost: '', 
            isNote: true 
          });
        }
        
        if (selectedAttachableVolumes && selectedAttachableVolumes.length > 0) {
          breakdown.push({ 
            label: 'Attached existing volumes are billed separately', 
            cost: '', 
            isNote: true 
          });
        }
      }

      return { storageCost, breakdown };
    };

    const { storageCost, breakdown: storageBreakdown } = calculateStorageDisplay();
    
    const priceMatch = price.match(/[\d.]+/);
    const pricePerMinute = priceMatch ? parseFloat(priceMatch[0]) : 0;
    const pricePerHour = pricePerMinute * 60;
    
    const gpuCost = pricePerHour;
    const totalPrice = gpuCost + storageCost;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-[#121212] rounded-lg w-full max-w-md border border-[#333333] max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-[#e0eacc]">
                Confirm VM Deployment
              </h3>
              {!isAdvancedRegion && publicIP && (
                <div className="relative group">
                  <span className="text-yellow-300 text-lg cursor-help">⚠️</span>
                  <div className="absolute left-1/2 -translate-x-1/2 top-8 hidden group-hover:block z-50 w-72 bg-yellow-900 bg-opacity-95 border border-yellow-800 rounded-md p-3 shadow-lg">
                    <p className="text-yellow-300 text-xs leading-relaxed">
                      <span className="font-bold">Public IP Security:</span> This VM will be accessible from the internet. Ensure proper security measures are in place.
                    </p>
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-yellow-900 border-l border-t border-yellow-800 transform rotate-45"></div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="bg-[#1a1a1a] p-4 rounded border border-[#333333] mb-4">
              <div className="space-y-2 text-sm">
                {Object.entries(configuration).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-gray-400">{key}:</span>
                    <span className="text-white">{truncateText(String(value), 200)}</span>
                  </div>
                ))}
                <div className="flex justify-between">
                  <span className="text-gray-400">Boot:</span>
                  <span 
                    className="text-white cursor-help" 
                    title={
                      selectedBootSource === 'volumes' && selectedVolume ? 
                        bootableVolumes.find(v => v.id === selectedVolume)?.name || 'Selected Volume' : 
                      selectedBootSource === 'custom_images' && selectedCustomImage ?
                        customImages.find(img => img.id === selectedCustomImage)?.name || 'Selected Custom Image' :
                        cleanOSName(osVersions.find(os => os.id === selectedOS)?.name) || 'Selected OS'
                    }
                  >
                    {selectedBootSource === 'volumes' && selectedVolume ? 
                      truncateForDisplay(bootableVolumes.find(v => v.id === selectedVolume)?.name || 'Selected Volume') : 
                    selectedBootSource === 'custom_images' && selectedCustomImage ?
                      truncateForDisplay(customImages.find(img => img.id === selectedCustomImage)?.name || 'Selected Custom Image') :
                      truncateForDisplay(cleanOSName(osVersions.find(os => os.id === selectedOS)?.name) || 'Selected OS')
                    }
                  </span>
                </div>
                
                {isAdvancedRegion && selectedScript && !(selectedBootSource === 'volumes' && selectedVolume) && 
                 !osVersions.find(os => os.id === selectedOS)?.is_user_volume && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Startup Script:</span>
                    <span className="text-white">
                      {truncateText(scripts.find(s => s.id === selectedScript)?.name || 'Selected Script', 100)}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-400">SSH Key:</span>
                  <span className="text-white">
                    {isAdvancedRegion && 
                     ((selectedBootSource === 'volumes' && selectedVolume) || 
                      osVersions.find(os => os.id === selectedOS)?.is_user_volume) ? 
                      'Not required' : 
                      sshKeys.find(key => key.id === selectedSSHKey)?.name || 'Selected Key'
                    }
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-[#1a1a1a] p-4 rounded border border-[#333333] mb-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">GPU:</span>
                  <span className="text-white">{getCurrencySymbol(currency)}{gpuCost.toFixed(4)}/hour</span>
                </div>
                
                <div 
                  className="flex justify-between items-center cursor-pointer hover:bg-[#222222] p-1 rounded transition-colors"
                  onClick={() => setShowStorageBreakdown(!showStorageBreakdown)}
                >
                  <span className="text-gray-400 flex items-center gap-1">
                    Storage:
                    <span className="text-xs text-gray-500">ⓘ</span>
                  </span>
                  <span className="text-white">{getCurrencySymbol(currency)}{storageCost.toFixed(4)}/hour</span>
                </div>
                
                {showStorageBreakdown && (
                  <div className="bg-[#0a0a0a] border border-[#444444] rounded p-3 mt-2 text-xs">
                    {storageBreakdown.map((item, index) => (
                      <div 
                        key={index} 
                        className={`flex justify-between mb-1 ${item.isFree ? 'text-green-400' : item.isNote ? 'text-gray-500 italic' : 'text-gray-300'}`}
                      >
                        <span>{item.label}</span>
                        <span>{item.cost}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="border-t border-[#333333] pt-2 mt-2">
                  <div className="flex justify-between font-semibold">
                    <span className="text-gray-300">Total:</span>
                    <span className="text-[#e0eacc]">{getCurrencySymbol(currency)}{totalPrice.toFixed(4)}/hour</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              {!showNameInput ? (
                <div className="text-center">
                  <button
                    onClick={() => setShowNameInput(true)}
                    className="text-gray-500 text-xs hover:text-gray-400 transition-colors"
                  >
                    + add custom name
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={localInstanceName}
                      onChange={handleNameChange}
                      placeholder="Enter VM name (optional)"
                      className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded text-white text-sm placeholder-gray-600 focus:outline-none transition-colors ${getBorderColor()} ${validationState === 'valid' ? 'focus:border-[#e0eacc]' : 'focus:border-red-400'}`}
                      maxLength={30}
                      autoFocus
                    />
                  </div>
                  
                  <div className="flex justify-end text-xs pr-3">
                    <span className={getCharCountColor()}>
                      {localInstanceName.length}/30
                      {localInstanceName.length > 0 && localInstanceName.length < 3 && (
                        <span className="text-red-400 ml-1">(min 3)</span>
                      )}
                    </span>
                  </div>
                  
                  <div className="pl-[10%]" style={{ marginTop: '-10px' }}>
                    <button
                      onClick={() => {
                        setShowNameInput(false);
                        setLocalInstanceName('');
                        setNameError('');
                      }}
                      className="text-gray-500 text-xs hover:text-gray-400 transition-colors"
                    >
                      ← use auto-generated name
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-1.5 bg-[#333333] hover:bg-[#444444] text-white rounded transition-colors text-[15px]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
                className={`px-4 py-1.5 rounded font-medium transition-colors text-[15px] ${
                  isConfirmDisabled 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-[#e0eacc] hover:bg-[#c8d9a9] text-black'
                }`}
                title={isConfirmDisabled ? 'Please fix the instance name errors' : 'Deploy VM'}
              >
                Confirm Deployment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleRetryClick = async () => {
    setError(null);
      
    await fetchSpecsAndStocks();
    
    if (showRetryProfile) {
      setShowRetryProfile(false);
      fetchUserCurrency();
    }
    if (showRetrySSHKeys) {
      setShowRetrySSHKeys(false);
      fetchSSHKeys();
    }
    if (showRetryScripts) {
      setShowRetryScripts(false);
      fetchScripts();
    }
    if (showRetryVolumes) {
      setShowRetryVolumes(false);
      fetchVolumes();
    }
    if (showRetryImages) {
      setShowRetryImages(false);
      fetchImagesForRegion();
    }
    if (showRetryDeploy) {
      setShowRetryDeploy(false);
      handleDeploy();
    }
  };

 return (
    <>
      <SEO
  title="Deploy GPU Instances in Minutes — H100 to B300 | Barrack"
  description="Deploy H100, H200, B200 and B300 GPU on-demand instances. Select your GPU, pick a region, and launch. Per-minute billing from $0.59/hr."
  path="/deploy"
/>
      <ResponsiveWrapper
        isRefreshing={loadingStates.main}
      error={error}
      isLoading={loadingStates.main}
      loadingText="Loading deployment options..."
      showRetry={showRetryProfile || showRetryDeploy || showRetrySSHKeys || showRetryScripts || showRetryVolumes || showRetryImages}
      onRetry={handleRetryClick}
    >
      {!isMobile && !isTablet ? (
        <div className="flex bg-black min-h-screen text-white barrack-container">
          <Sidebar 
            activePage="deploy" 
            isMobile={isMobile}
            isTablet={isTablet}
            isSidebarOpen={false}
            toggleSidebar={() => {}}
            authenticated={isAuthenticated}
            onAuthAction={() => {}}
          />
          
          <div className="flex-1">
            <div className="flex-1 p-2">
<div className="grid grid-cols-12 gap-2 w-full">                <div className="col-span-9">
                 <GPUSelection
  gpuTypes={visibleGpuTypes}  // was: gpuTypes
  selectedGPU={selectedGPU}
  onSelectGPU={handleGPUSelection}
  isLoading={loadingStates.main || loadingStates.gpu}
/>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <LocationSelector
                      locations={regions}
                      selectedLocation={selectedRegion}
                      onSelectLocation={handleLocationSelection}
                      isLoading={loadingStates.main || loadingStates.location}
                    />
                    
                    <GPUCountSelector
                      counts={gpuCounts}
                      selectedCount={selectedCount}
                      onSelectCount={handleGPUCountSelection}
                      disabled={gpuCounts.length === 0}
                      isLoading={loadingStates.main || loadingStates.gpuCount}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <BootSourceSelector
                      sources={bootSources}
                      selectedSource={selectedBootSource}
                      onSelectSource={handleBootSourceChange}
                      osVersions={osVersions.filter(os => {
                        if (!os.is_user_volume) return true;
                        const volumeId = os.volume_id;
                        return !selectedAttachableVolumes.includes(volumeId);
                      })}
                      selectedOS={selectedOS}
                      onSelectOS={handleOSSelection}
                      selectedRegion={selectedRegion}
                      volumes={volumes}
                      selectedVolume={selectedVolume}
                      onSelectVolume={handleVolumeSelection}
                      onNavigateToVolumes={handleNavigateToVolumes}
                      customImages={customImages}
                      selectedCustomImage={selectedCustomImage}
                      onSelectCustomImage={handleCustomImageSelection}
                      onOsVolumeSizeChange={handleOsVolumeSizeChange}
                      onNewVolumesChange={handleNewVolumesChange}
                      onAttachableVolumesChange={handleAttachableVolumesChange}
                    />
                    
                    {isAuthenticated ? (
                      <SSHKeySelector
                        selectedKey={selectedSSHKey}
                        onKeyChange={handleSSHKeySelection}
                        onImportKey={handleImportSSHKey}
                        onCreateKey={handleCreateSSHKey}
                        onRefresh={refreshSSHKeysFromParent}
                        sshKeys={sshKeys}
                        disabled={isAdvancedRegion && osVersions.find(os => os.id === selectedOS)?.is_user_volume}
                        disabledMessage="SSH key not required - using existing OS volume configuration"
                        isModalOpen={sshKeyModalOpen}
                      />
                    ) : (
                      <div className="bg-[#121212] border border-[#333333] rounded-lg p-4">
                        <h3 className="text-white font-medium mb-2">SSH Keys</h3>
                        <div className="flex justify-center">
                          <button
                            onClick={() => {
                              logUserAction('signup_button_clicked', { source: 'ssh_keys_section' });
                              navigate('/signup');
                            }}
                            className="mt-2 bg-[#e0eacc] text-black px-3 py-1.5 rounded-[0.5rem] text-sm font-medium hover:bg-[#c8d9a9] transition-colors focus:outline-none focus:ring-2 focus:ring-[#e0eacc]"
                          >
                            Sign up to manage SSH keys →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <AdvancedConfiguration
                    publicIP={publicIP}
                    onTogglePublicIP={handlePublicIPToggle}
                    onManageFirewall={handleOpenFirewallModal}
                    onAddCloudInit={handleAddCloudInit}
                    securityRules={securityRules}
                    onUpdateSecurityRules={setSecurityRules}
                    isAdvancedRegion={isAdvancedRegion}
                    isAuthenticated={isAuthenticated}
                    scripts={scripts}
                    selectedScript={selectedScript}
                    onScriptChange={handleScriptSelection}
                    onCreateScript={handleCreateScript}
                    onEditScript={handleEditScript}
                    onDeleteScript={handleDeleteScript}
                    onRefreshScripts={refreshScriptsFromParent}
                    loadingScripts={loadingStates.scripts}
                    onBulkDeleteScripts={handleBulkDeleteScripts}
                  />
                </div>
                
                <div className="col-span-3">
                  <SummaryCard
                    configuration={configuration}
                    price={price}
                    currency={priceCurrency}
                    bootSource={
                      selectedBootSource === 'volumes' && selectedVolume ? 
                        truncateText(bootableVolumes.find(v => v.id === selectedVolume)?.name || 'Selected Volume', 100) : 
                      selectedBootSource === 'custom_images' && selectedCustomImage ?
                        truncateText(customImages.find(img => img.id === selectedCustomImage)?.name || 'Selected Custom Image', 100) :
                        truncateText(osVersions.find(os => os.id === selectedOS)?.name || 'Selected OS', 100)
                    }
                    isLoading={loadingStates.main || loadingStates.summary}
                  />
                  <div className="flex justify-center">
                    <button
                      onClick={handleDeployClick}
                      disabled={(!isAuthenticated ? false : (!deployEnabled || insufficientBalance)) || loadingStates.deploying}
                      title={deployButtonTitle}
                      className={`
                        w-[95%] 
                        py-2 
                        px-4 
                        rounded-full
                        font-semibold 
                        uppercase
                        tracking-wider
                        transition-all
                        font-mono
                        ${
                          (!isAuthenticated || (deployEnabled && !insufficientBalance)) && !loadingStates.deploying
                            ? 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9] shadow-md'
                            : 'bg-gray-700 cursor-not-allowed text-gray-400 opacity-75'
                        }
                      `}
                    >
                      {loadingStates.deploying ? 'DEPLOYING...' : isAuthenticated ? (isProfileComplete ? 'DEPLOY' : 'COMPLETE PROFILE') : 'SIGN UP TO DEPLOY'}
                    </button>
                  </div>
        
              {isAuthenticated && (!deployEnabled || insufficientBalance) && selectedGPU && selectedRegion && selectedCount && (
                    <p className="text-yellow-500 text-sm mt-1">
                      {insufficientBalance && deployEnabled
                        ? `Insufficient balance. You need at least ${getCurrencySymbol(userCurrency)}${totalHourlyCost.toFixed(4)}/hr to deploy this configuration.`
                        : isAdvancedRegion && osVersions.find(os => os.id === selectedOS)?.is_user_volume && selectedSSHKey
                        ? "SSH key should not be selected when using existing OS volume"
                        : !selectedSSHKey 
                          ? "SSH key is required. Please select or create an SSH key."
                          : selectedBootSource === 'os_images' && !selectedOS
                            ? "OS image selection is required."
                            : selectedBootSource === 'volumes' && !selectedVolume
                              ? "Volume selection is required. Please select a volume."
                              : selectedBootSource === 'custom_images' && !selectedCustomImage
                                ? "Custom image is required. Please select an image."
                                : "This configuration is currently unavailable or missing required information."}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4 deploy-container">
        <GPUSelection
  gpuTypes={visibleGpuTypes}  // was: gpuTypes
  selectedGPU={selectedGPU}
  onSelectGPU={handleGPUSelection}
  isLoading={loadingStates.main || loadingStates.gpu}
/>
          
          <div className="grid grid-cols-1 gap-4">
            <LocationSelector
              locations={regions}
              selectedLocation={selectedRegion}
              onSelectLocation={handleLocationSelection}
              isLoading={loadingStates.main || loadingStates.location}
            />
            
            <GPUCountSelector
              counts={gpuCounts}
              selectedCount={selectedCount}
              onSelectCount={handleGPUCountSelection}
              disabled={gpuCounts.length === 0}
              isLoading={loadingStates.main || loadingStates.gpuCount}
            />
          </div>
          
          <BootSourceSelector
            sources={bootSources}
            selectedSource={selectedBootSource}
            onSelectSource={handleBootSourceChange}
            osVersions={osVersions.filter(os => {
              if (!os.is_user_volume) return true;
              const volumeId = os.volume_id;
              return !selectedAttachableVolumes.includes(volumeId);
            })}
            selectedOS={selectedOS}
            onSelectOS={handleOSSelection}
            selectedRegion={selectedRegion}
            volumes={volumes}
            selectedVolume={selectedVolume}
            onSelectVolume={handleVolumeSelection}
            onNavigateToVolumes={handleNavigateToVolumes}
            customImages={customImages}
            selectedCustomImage={selectedCustomImage}
            onSelectCustomImage={handleCustomImageSelection}
            onOsVolumeSizeChange={handleOsVolumeSizeChange}
            onNewVolumesChange={handleNewVolumesChange}
            onAttachableVolumesChange={handleAttachableVolumesChange}
          />
          
          {isAuthenticated ? (
            <SSHKeySelector
              selectedKey={selectedSSHKey}
              onKeyChange={handleSSHKeySelection}
              onImportKey={handleImportSSHKey}
              onCreateKey={handleCreateSSHKey}
              onRefresh={refreshSSHKeysFromParent}
              sshKeys={sshKeys}
              disabled={isAdvancedRegion && osVersions.find(os => os.id === selectedOS)?.is_user_volume}
              disabledMessage="SSH key not required - using existing OS volume configuration"
              isModalOpen={sshKeyModalOpen}
            />
          ) : (
            <div className="bg-[#121212] border border-[#333333] rounded-lg p-4">
              <h3 className="text-white font-medium mb-2">SSH Keys</h3>
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    logUserAction('signup_button_clicked', { source: 'ssh_keys_section_mobile' });
                    navigate('/signup');
                  }}
                  className="mt-2 bg-[#e0eacc] text-black px-3 py-1.5 rounded-[0.5rem] text-sm font-medium hover:bg-[#c8d9a9] transition-colors focus:outline-none focus:ring-2 focus:ring-[#e0eacc]"
                >
                  Sign up to manage SSH keys →
                </button>
              </div>
            </div>
          )}
          
          <AdvancedConfiguration
            publicIP={publicIP}
            onTogglePublicIP={handlePublicIPToggle}
            onManageFirewall={handleOpenFirewallModal}
            onAddCloudInit={handleAddCloudInit}
            securityRules={securityRules}
            onUpdateSecurityRules={setSecurityRules}
            isAdvancedRegion={isAdvancedRegion}
            isAuthenticated={isAuthenticated}
            scripts={scripts}
            selectedScript={selectedScript}
            onScriptChange={handleScriptSelection}
            onCreateScript={handleCreateScript}
            onEditScript={handleEditScript}
            onDeleteScript={handleDeleteScript}
            onRefreshScripts={refreshScriptsFromParent}
            loadingScripts={loadingStates.scripts}
            onBulkDeleteScripts={handleBulkDeleteScripts}
          />
          
          <SummaryCard
            configuration={configuration}
            price={price}
            currency={priceCurrency}
            bootSource={
              selectedBootSource === 'volumes' && selectedVolume ? 
                truncateText(bootableVolumes.find(v => v.id === selectedVolume)?.name || 'Selected Volume', 100) : 
              selectedBootSource === 'custom_images' && selectedCustomImage ?
                truncateText(customImages.find(img => img.id === selectedCustomImage)?.name || 'Selected Custom Image', 100) :
                truncateText(osVersions.find(os => os.id === selectedOS)?.name || 'Selected OS', 100)
            }
            isLoading={loadingStates.main || loadingStates.summary}
          />
          
          <div className="flex justify-center">
            <button
              onClick={handleDeployClick}
              disabled={(!isAuthenticated ? false : (!deployEnabled || insufficientBalance)) || loadingStates.deploying}
              title={deployButtonTitle}
              className={`
                w-full 
                py-3 
                px-4 
                rounded-full
                font-semibold 
                uppercase
                tracking-wider
                transition-all
                font-mono
                ${
                  (!isAuthenticated || (deployEnabled && !insufficientBalance)) && !loadingStates.deploying
                    ? 'bg-[#e0eacc] text-black hover:bg-[#c8d9a9] shadow-md'
                    : 'bg-gray-700 cursor-not-allowed text-gray-400 opacity-75'
                }
              `}
            >
              {loadingStates.deploying ? 'DEPLOYING...' : isAuthenticated ? (isProfileComplete ? 'DEPLOY' : 'COMPLETE PROFILE') : 'SIGN UP TO DEPLOY'}
            </button>
          </div>
          
          {isAuthenticated && (!deployEnabled || insufficientBalance) && selectedGPU && selectedRegion && selectedCount && (
            <p className="text-yellow-500 text-sm text-center">
              {insufficientBalance && deployEnabled
                ? `Insufficient balance. You need at least ${getCurrencySymbol(userCurrency)}${totalHourlyCost.toFixed(4)}/hr to deploy this configuration.`
                : isAdvancedRegion && osVersions.find(os => os.id === selectedOS)?.is_user_volume && selectedSSHKey
                ? "SSH key should not be selected when using existing OS volume"
                : !selectedSSHKey 
                  ? "SSH key is required. Please select or create an SSH key."
                  : selectedBootSource === 'os_images' && !selectedOS
                    ? "OS image selection is required."
                    : selectedBootSource === 'volumes' && !selectedVolume
                      ? "Volume selection is required. Please select a volume."
                      : selectedBootSource === 'custom_images' && !selectedCustomImage
                        ? "Custom image is required. Please select an image."
                        : "This configuration is currently unavailable or missing required information."}
            </p>
          )}
        </div>
      )}
      
      <FirewallModal 
        isOpen={firewallModalOpen}
        onClose={() => {
          setFirewallModalOpen(false);
          
          logUserAction('firewall_modal_closed', {
            currentRulesCount: securityRules.length
          });
          
          logInfo('Firewall modal closed', {
            currentRulesCount: securityRules.length
          });
        }}
        onSave={handleFirewallSave}
      />
      
      {isAuthenticated && (
        <SSHKeyModal
          isOpen={sshKeyModalOpen}
          type={sshKeyModalType}
          onClose={() => {
            setSSHKeyModalOpen(false);
            
            logUserAction('ssh_key_modal_closed', {
              modalType: sshKeyModalType,
              currentKeysCount: sshKeys.length
            });
            
            logInfo('SSH key modal closed', {
              modalType: sshKeyModalType,
              currentKeysCount: sshKeys.length
            });
          }}
          onSave={handleSSHKeySave}
          existingKeyNames={sshKeys.map(k => k.name)}
        />
      )}

      {isAuthenticated && isAdvancedRegion && (
        <ScriptModal
          isOpen={scriptModalOpen}
          type={scriptModalType}
          onClose={() => {
            setScriptModalOpen(false);
            setEditingScript(null);
            
            logUserAction('script_modal_closed', {
              modalType: scriptModalType,
              currentScriptsCount: scripts.length
            });
            
            logInfo('Script modal closed', {
              modalType: scriptModalType,
              currentScriptsCount: scripts.length
            });
          }}
          onSave={handleScriptModalSave}
          editingScript={editingScript}
          scripts={scripts}
        />
      )}
      
      {cloudInitModalOpen && (
        <CloudInitModal
          isOpen={cloudInitModalOpen}
          onClose={() => {
            setCloudInitModalOpen(false);
            
            logUserAction('cloud_init_modal_closed', {
              hasScript: !!cloudInitScript,
              scriptLength: cloudInitScript.length
            });
            
            logInfo('Cloud-init modal closed', {
              hasScript: !!cloudInitScript,
              scriptLength: cloudInitScript.length
            });
          }}
          onSave={handleCloudInitSave}
          initialScript={cloudInitScript}
        />
      )}
      
      <DeployConfirmationDialog
        isOpen={confirmDeploy}
        onConfirm={(instanceName) => {
          setConfirmDeploy(false);
          handleDeploy(instanceName);
        }}
        onCancel={() => setConfirmDeploy(false)}
        currency={userCurrency}
        configuration={configuration}
        price={price}
        isAdvancedRegion={isAdvancedRegion}
        publicIP={publicIP}
        selectedBootSource={selectedBootSource}
        selectedVolume={selectedVolume}
        bootableVolumes={bootableVolumes}
        selectedCustomImage={selectedCustomImage}
        customImages={customImages}
        osVersions={osVersions}
        selectedOS={selectedOS}
        selectedScript={selectedScript}
        scripts={scripts}
        sshKeys={sshKeys}
        selectedSSHKey={selectedSSHKey}
        newVolumes={newVolumes}
        osVolumeSize={osVolumeSize}
        selectedAttachableVolumes={selectedAttachableVolumes}
        truncateText={truncateText}
        getCurrencySymbol={getCurrencySymbol}
        getRatesForCurrency={getRatesForCurrency}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        title="Delete Script"
        message={`Are you sure you want to delete "${scriptToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete Script"
        cancelText="Cancel"
        isDangerous={true}
        onConfirm={confirmDeleteScript}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setScriptToDelete(null);
          logUserAction('delete_script_cancelled', { scriptId: scriptToDelete?.id });
        }}
      />
</ResponsiveWrapper>
    </>
  );};

export default DeployVM;